import React, { useState, useEffect, useMemo, useRef, cloneElement } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import resultsService from '../../services/resultsService';
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import { categoriesService, ageGroupsService, groupsService } from '../../services';
import cacheService from '../../utils/cacheService';
import { getAgeGroupColors } from "../../utils/ageGroupColors";
import resultsStreamService from '../../services/resultsStreamService';
import GenericSelectTailwind from "./GenericSelectTailwind";
import Toast from "./Toast";

const JudgeScoresPanel = ({ participant, event, updatedValues }) => {
  const penaltyValue = parseFloat(participant.penalty) || 0;
  const formattedPenalty = Math.abs(penaltyValue).toFixed(2);
  const isNegative = penaltyValue < 0;

  return (
    <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700/60">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
          <i className="fas fa-gavel text-[9px]"></i>
          Wertungsrichter
        </span>
        {penaltyValue !== 0 && (
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition-all duration-300 ${
            updatedValues.has(`penalty-${participant.id}-${event.id}`) ? 'animate-pulse' : ''
          } ${
            isNegative
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-700/50'
              : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700/50'
          }`}>
            <i className={`fas fa-${isNegative ? 'minus' : 'plus'} text-[8px]`}></i>
            Strafe: {isNegative ? '-' : '+'}{formattedPenalty}
          </span>
        )}
      </div>
      <div className="-mx-0.5 overflow-x-auto sm:mx-0 sm:overflow-visible">
        <div className="grid min-w-[260px] grid-cols-5 gap-1.5 sm:min-w-0 sm:gap-2">
        {participant.judgeScores.map((score, judgeIndex) => {
          const scoreValue = parseFloat(score) || 0;
          const formattedScore = scoreValue.toFixed(2);
          const isUpdated = updatedValues.has(`judgeScore${judgeIndex + 1}-${participant.id}-${event.id}`);
          return (
            <div
              key={judgeIndex}
              className={`flex flex-col items-center rounded-xl border py-2 px-1.5 transition-all duration-300 ${
                isUpdated
                  ? 'animate-pulse bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700/50'
                  : 'bg-slate-50 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600/50'
              }`}
            >
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 leading-none mb-1.5">
                J{judgeIndex + 1}
              </span>
              <span className={`text-sm font-bold leading-none tabular-nums ${
                isUpdated
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-slate-800 dark:text-slate-100'
              }`}>
                {formattedScore}
              </span>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

/**
 * Mobil: Zeile 1 = Rang links, Punkte + Chevron rechts; Zeile 2 = Flagge + Name/Land.
 * Ab sm: eine Zeile (Rang · Flagge · Text | Punkte · Chevron).
 */
function ParticipantResultCardLayout({ rankBadge, flagImg, body, scoreBadge, expanded }) {
  const chevron = (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center sm:h-9 sm:w-9"
      aria-hidden
    >
      <i
        className={`fas fa-chevron-down text-xs text-slate-400 transition-transform duration-200 dark:text-slate-500 ${expanded ? "rotate-180" : ""}`}
      />
    </span>
  );

  return (
    <div className="px-4 py-3.5">
      {/* Mobil */}
      <div className="sm:hidden">
        <div className="mb-3 flex items-center justify-between gap-2">
          {cloneElement(rankBadge, { key: "rank-mobile-top" })}
          <div className="flex min-w-0 shrink-0 items-center gap-1">
            {scoreBadge}
            {chevron}
          </div>
        </div>
        <div className="flex min-w-0 items-start gap-3">
          {flagImg}
          <div className="min-w-0 flex-1">{body}</div>
        </div>
      </div>

      {/* Tablet/Desktop */}
      <div className="hidden sm:flex sm:flex-row sm:items-center sm:gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {rankBadge}
          {flagImg}
          <div className="min-w-0 flex-1">{body}</div>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2">
          {scoreBadge}
          {chevron}
        </div>
      </div>
    </div>
  );
}

const ResultsTailwind = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Prüfe ob es sich um die öffentliche Ansicht handelt
  const isPublicView = location.pathname === "/results";

  // Redirect Scorekeeper zur ScorekeeperView
  useEffect(() => {
    if (user && user.role && user.role.toLowerCase() === 'scorekeeper') {
      navigate('/scorekeeper', { replace: true });
    }
  }, [user, navigate]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedDay, setSelectedDay] = useState(null); // Initial null, wird vom Effect gesetzt
  const [selectedViewMode, setSelectedViewMode] = useState("all"); // "all", "schedule", "results"
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [resultsData, setResultsData] = useState([]);
  const [allResultsData, setAllResultsData] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [expandedJudgeScores, setExpandedJudgeScores] = useState(new Set());
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [expandedResults, setExpandedResults] = useState(new Set()); // Erweiterte Ergebnisse (zeigt alle Teilnehmer, nicht nur Top 3)
  const [expandedNations, setExpandedNations] = useState(new Set()); // Erweiterte Nationen in Team-Events
  const [showAllJudgeScores, setShowAllJudgeScores] = useState(false); // Globale Option: Detail-Punkte anzeigen
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [teamData, setTeamData] = useState(null); // Team-Daten für Team-Events
  const [partnerTeamsData, setPartnerTeamsData] = useState(new Map()); // Partner-Teams-Daten pro Gruppe (Map von groupId -> { partnerTeams: [...] })
  const [loading, setLoading] = useState(true);
  const [updatedValues, setUpdatedValues] = useState(new Set()); // Track welche Werte sich geändert haben
  const [lastUpdateTimes, setLastUpdateTimes] = useState(new Map()); // Track letzte Aktualisierungszeit pro Gruppe (Map von groupId -> Date)
  const previousPositionsRef = useRef(new Map()); // Track vorherige Positionen für Animationen (Map von eventId -> Map von participantId -> position)
  const activeGroupConnectionsRef = useRef(new Map()); // Track aktive SSE-Verbindungen (Map von groupId -> cleanup function)


  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadCategoriesAndAgeGroups();
    loadAllData();
  }, []);

  // Lese Filter aus URL-Parameter und setze Filter
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    const ageGroupFromUrl = searchParams.get('ageGroup');
    const genderFromUrl = searchParams.get('gender');
    const locationFromUrl = searchParams.get('location');

    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
    if (ageGroupFromUrl) {
      setSelectedAgeGroup(ageGroupFromUrl);
    }
    if (genderFromUrl) {
      setSelectedGender(genderFromUrl);
    }
    if (locationFromUrl) {
      setSelectedLocation(locationFromUrl);
    }
  }, [searchParams]);

  // Lade Kategorien und Altersgruppen (mit Cache)
  const loadCategoriesAndAgeGroups = async () => {
    try {
      // Prüfe Cache zuerst
      const cachedCategories = cacheService.get('categories');
      const cachedAgeGroups = cacheService.get('ageGroups');

      if (cachedCategories && cachedAgeGroups) {
        setCategories(cachedCategories);
        const validAgeGroups = cachedAgeGroups.filter(ag => ag.name !== 'Alle');
        setAgeGroups(validAgeGroups);
        return; // Verwende Cache, kein Request nötig
      }

      // Cache nicht vorhanden oder abgelaufen, lade vom Backend
      const [categoriesResult, ageGroupsResult] = await Promise.all([
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (categoriesResult.success) {
        const categoriesData = categoriesResult.data || [];
        setCategories(categoriesData);
        cacheService.set('categories', categoriesData);
      } else {
        console.error('Fehler beim Laden der Kategorien:', categoriesResult.error);
        setCategories([]);
      }

      if (ageGroupsResult.success) {
        const allAgeGroups = ageGroupsResult.data || [];
        const validAgeGroups = allAgeGroups.filter(ag => ag.name !== 'Alle');
        setAgeGroups(validAgeGroups);
        cacheService.set('ageGroups', allAgeGroups);
      } else {
        console.error('Fehler beim Laden der Altersgruppen:', ageGroupsResult.error);
        setAgeGroups([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien und Altersgruppen:', error);
      setCategories([]);
      setAgeGroups([]);
    }
  };

  // Lade alle notwendigen Daten
  const loadAllData = async () => {
    try {
      setLoading(true);

      // Lade Schedule-Daten (für Events mit Status "Ausstehend")
      let loadedScheduleData = [];
      try {
        const scheduleResult = await scheduleService.getAllScheduleEvents();
        if (scheduleResult.success && scheduleResult.data) {
          loadedScheduleData = scheduleResult.data || [];
          setScheduleData(loadedScheduleData);
        } else {
          setScheduleData([]);
        }
      } catch (error) {
        console.warn('Backend-Fehler beim Laden der Schedule-Daten:', error.message);
        setScheduleData([]);
      }

      // Lade Teilnehmer-Daten
      try {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success && participantsResult.data) {
          setParticipants(participantsResult.data || []);
        } else {
          setParticipants([]);
        }
      } catch (error) {
        console.warn('Backend-Fehler beim Laden der Teilnehmer-Daten:', error.message);
        setParticipants([]);
      }

      // Lade alle Ergebnisse ohne Filter (nur einmal beim initialen Laden)
      // loadResultsData() wird separat im useEffect mit Filter-Dependencies aufgerufen
      await loadAllResultsData();

      // Prüfe ob Team-Events vorhanden sind und lade Team-Daten
      const hasTeamEvent = loadedScheduleData.some(event =>
        event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb'
      );

      if (hasTeamEvent) {
        try {
          const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
          if (teamGroupResponse.success && teamGroupResponse.data) {
            setTeamData(teamGroupResponse.data);
          }
        } catch (error) {
          console.debug('Fehler beim Laden der Team-Daten (nicht kritisch):', error);
        }
      }

      // Lade Partner-Teams für alle Partnerform-Gruppen
      const partnerFormGroups = loadedScheduleData.filter(event =>
        event.group?.category?.type === 'Partnerform' && event.group?.id
      );

      if (partnerFormGroups.length > 0) {
        const partnerTeamsMap = new Map();
        await Promise.all(
          partnerFormGroups.map(async (event) => {
            try {
              const groupResponse = await groupsService.getGroupWithStartOrder(event.group.id);
              if (groupResponse.success && groupResponse.data?.isPartnerForm && groupResponse.data?.partnerTeams) {
                partnerTeamsMap.set(event.group.id, groupResponse.data);
              }
            } catch (error) {
              console.debug(`Fehler beim Laden der Partner-Teams für Gruppe ${event.group.id} (nicht kritisch):`, error);
            }
          })
        );
        setPartnerTeamsData(partnerTeamsMap);
      }
    } catch (error) {
      console.error('Unerwarteter Fehler beim Laden der Daten:', error);
      // Setze leere Arrays statt Fallback-Daten
      setScheduleData([]);
      setParticipants([]);
      setResultsData([]);
    } finally {
      setLoading(false);
    }
  };

  // Lade Ergebnisse von der Backend-API
  const loadResultsData = async () => {
    try {
      const filters = {};
      if (selectedCategory !== "all") filters.category = selectedCategory;
      if (selectedAgeGroup !== "all") filters.ageGroup = selectedAgeGroup;
      if (selectedGender !== "all") filters.gender = selectedGender;
      if (selectedLocation !== "all") filters.venue = selectedLocation;

      const result = await resultsService.getResultsGroupedByEvents(filters);

      if (result.success && result.data) {
        // Behalte Live-Updates für aktive Events (werden durch SSE aktualisiert)
        setResultsData(prevData => {
          const newData = result.data || [];

          // Erstelle eine Map der aktiven Event-IDs aus scheduleData
          const activeEventIds = new Set();
          scheduleData.forEach(event => {
            if (event.status === 'Aktiv') {
              const groupId = event.group?.id || event.id;
              activeEventIds.add(groupId);
            }
          });

          // Behalte aktive Events aus prevData (mit Live-Updates)
          const activeEventsFromPrev = prevData.filter(event => {
            const eventId = event.id || event.group?.id;
            return activeEventIds.has(eventId);
          });

          // Kombiniere: Aktive Events aus prevData + neue gefilterte Events (ohne aktive)
          const newEventsWithoutActive = newData.filter(event => {
            const eventId = event.id || event.group?.id;
            return !activeEventIds.has(eventId);
          });

          return [...activeEventsFromPrev, ...newEventsWithoutActive];
        });
      } else {
        // Behalte nur aktive Events, wenn Laden fehlschlägt
        setResultsData(prevData => {
          const activeEventIds = new Set();
          scheduleData.forEach(event => {
            if (event.status === 'Aktiv') {
              const groupId = event.group?.id || event.id;
              activeEventIds.add(groupId);
            }
          });

          return prevData.filter(event => {
            const eventId = event.id || event.group?.id;
            return activeEventIds.has(eventId);
          });
        });
      }
    } catch (error) {
      console.warn('Backend-Fehler beim Laden der Ergebnisse:', error.message);
      // Behalte nur aktive Events bei Fehler
      setResultsData(prevData => {
        const activeEventIds = new Set();
        scheduleData.forEach(event => {
          if (event.status === 'Aktiv') {
            const groupId = event.group?.id || event.id;
            activeEventIds.add(groupId);
          }
        });

        return prevData.filter(event => {
          const eventId = event.id || event.group?.id;
          return activeEventIds.has(eventId);
        });
      });
    }
  };

  // Lade alle Ergebnisse ohne Filter (für zuverlässige Anzeige abgeschlossener Gruppen)
  const loadAllResultsData = async () => {
    try {
      const result = await resultsService.getResultsGroupedByEvents({});
      if (result.success) {
        setAllResultsData(result.data || []);
      }
    } catch (error) {
      // Im Fehlerfall leeres Array belassen; UI fällt auf participants-View zurück
    }
  };

  // Lade Daten neu wenn sich Filter ändern
  useEffect(() => {
    loadResultsData();
  }, [selectedCategory, selectedAgeGroup, selectedGender, selectedLocation]);

  // SSE-Verbindungen für aktive Gruppen einrichten
  useEffect(() => {
    // Finde alle aktiven Gruppen aus scheduleData (nicht nur resultsData, da aktive Events möglicherweise noch keine Ergebnisse haben)
    const activeGroups = new Set();
    scheduleData.forEach(event => {
      if (event.status === 'Aktiv' && event.group?.id) {
        activeGroups.add(event.group.id);
      }
    });

    // Füge auch aktive Gruppen aus resultsData hinzu (falls vorhanden)
    resultsData.forEach(event => {
      if (event.status === 'Aktiv' && event.group?.id) {
        activeGroups.add(event.group.id);
      }
    });

    // Trenne Verbindungen für Gruppen, die nicht mehr aktiv sind
    activeGroupConnectionsRef.current.forEach((cleanup, groupId) => {
      if (!activeGroups.has(groupId)) {
        cleanup();
        activeGroupConnectionsRef.current.delete(groupId);
      }
    });

    // Erstelle neue Verbindungen für aktive Gruppen
    activeGroups.forEach(groupId => {
      if (!activeGroupConnectionsRef.current.has(groupId)) {
        // Handler für SSE-Updates
        const handleUpdate = (update) => {
          console.log('SSE: Update empfangen für Gruppe', groupId, 'Typ:', update.type, 'Update:', update);

          if (update.type === 'error') {
            console.error('SSE-Fehler:', update.message);
            return;
          }

          if (update.type === 'connected') {
            console.log('SSE-Verbindung hergestellt für Gruppe', update.groupId);
            return;
          }

          if (update.type === 'heartbeat') {
            // Heartbeat ignorieren
            return;
          }

          if (update.type === 'result-update') {
            // Einzelnes Result-Update verarbeiten
            const result = update.result;
            // Für Partner-Teams: ID ist `partner-team-${partnerTeamId}`
            const participantId = result.partnerTeamId
              ? `partner-team-${result.partnerTeamId}`
              : (result.participantId || (result.nationId ? `nation-${result.nationId}` : null));

            console.log('SSE: Result-Update empfangen für Gruppe', groupId, 'Teilnehmer', participantId, 'Result:', result);
            console.log('SSE: Result-Update Details:', {
              judgeScore1: result.judgeScore1,
              judgeScore2: result.judgeScore2,
              judgeScore3: result.judgeScore3,
              judgeScore4: result.judgeScore4,
              judgeScore5: result.judgeScore5,
              penalty: result.penalty,
              rank: result.rank
            });

            // Lade Ergebnisse für diese Gruppe neu, um sicherzustellen, dass alle Daten synchronisiert sind
            resultsService.getResultsByGroup(groupId).then(resultsResult => {
              if (resultsResult.success && resultsResult.data) {
                console.log('SSE: Ergebnisse für Gruppe neu geladen:', resultsResult.data.length, 'Ergebnisse');
                // Aktualisiere resultsData mit den neu geladenen Daten
                setResultsData(prevData => {
                  const newData = prevData.map(event => {
                    const eventGroupId = event.id || event.group?.id;
                    if (eventGroupId === groupId) {
                      // Erstelle Teilnehmer-Map aus den geladenen Ergebnissen
                      const participantsMap = new Map();
                      resultsResult.data.forEach(r => {
                        // Für Partner-Teams: ID ist `partner-team-${partnerTeamId}`
                        // Für Nationen: ID ist `nation-${nationId}`
                        // Für normale Teilnehmer: ID ist `participantId`
                        const pid = r.partnerTeamId
                          ? `partner-team-${r.partnerTeamId}`
                          : (r.participantId || (r.nationId ? `nation-${r.nationId}` : null));
                        if (pid) {
                          const judgeScores = [
                            parseFloat(r.judgeScore1) || 0,
                            parseFloat(r.judgeScore2) || 0,
                            parseFloat(r.judgeScore3) || 0,
                            parseFloat(r.judgeScore4) || 0,
                            parseFloat(r.judgeScore5) || 0
                          ];
                          const penalty = parseFloat(r.penalty) || 0;
                          const validScores = judgeScores.filter(score => score > 0);
                          const averageScore = validScores.length > 0
                            ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
                            : 0;
                          const finalScore = averageScore + penalty;

                          participantsMap.set(pid, {
                            judgeScores,
                            penalty,
                            score: finalScore.toFixed(2),
                            rank: r.rank || null,
                            didNotStart: r.didNotStart || false,
                            isLate: r.isLate || false
                          });
                        }
                      });

                      // Aktualisiere Teilnehmer mit den neuen Daten
                      const updatedParticipants = (event.participants || []).map(p => {
                        const updatedData = participantsMap.get(p.id);
                        if (updatedData) {
                          console.log('SSE: Neuladen - Aktualisiere Teilnehmer', p.id, 'Score:', p.score, '->', updatedData.score);
                          return {
                            ...p,
                            judgeScores: updatedData.judgeScores,
                            penalty: updatedData.penalty,
                            score: updatedData.score, // Bereits als String formatiert
                            rank: updatedData.rank,
                            didNotStart: updatedData.didNotStart,
                            isLate: updatedData.isLate
                          };
                        }
                        return p;
                      });

                      return {
                        ...event,
                        participants: updatedParticipants
                      };
                    }
                    return event;
                  });

                  return newData;
                });
              }
            }).catch(err => {
              console.error('SSE: Fehler beim Neuladen der Ergebnisse:', err);
            });

            // Aktualisiere letzte Aktualisierungszeit
            setLastUpdateTimes(prev => {
              const newMap = new Map(prev);
              newMap.set(groupId, new Date());
              return newMap;
            });

            if (participantId) {
              // Aktualisiere resultsData
              setResultsData(prevData => {
                console.log('SSE: Aktualisiere resultsData, vorher:', prevData.length, 'Events');

                // Prüfe ob Event bereits existiert
                let eventExists = prevData.some(e => {
                  const eventGroupId = e.id || e.group?.id;
                  return eventGroupId === groupId;
                });
                console.log('SSE: Event existiert in resultsData:', eventExists, 'groupId:', groupId);

                // Wenn Event nicht existiert, erstelle es aus scheduleData
                if (!eventExists) {
                  const scheduleEvent = scheduleData.find(e => {
                    const eventGroupId = e.group?.id || e.id;
                    return eventGroupId === groupId && e.status === 'Aktiv';
                  });

                  if (scheduleEvent) {
                    console.log('SSE: Event nicht in resultsData gefunden, erstelle aus scheduleData');
                    // Lade Teilnehmer für dieses Event
                    const eventParticipants = getParticipantsForEvent(scheduleEvent);
                    const newEvent = {
                      id: groupId,
                      group: scheduleEvent.group,
                      status: 'Aktiv',
                      venue: scheduleEvent.venue,
                      participants: eventParticipants.map(p => ({
                        id: p.id,
                        name: p.name,
                        nation: p.nation?.name || 'Unbekannt',
                        nationCode: p.nation?.code || 'DE',
                        judgeScores: [0, 0, 0, 0, 0],
                        penalty: 0,
                        score: 0,
                        rank: 0
                      }))
                    };

                    // Füge das neue Event zu prevData hinzu
                    const dataWithNewEvent = [...prevData, newEvent];
                    prevData = dataWithNewEvent;
                    eventExists = true;
                    console.log('SSE: Neues Event erstellt, Teilnehmer:', newEvent.participants.length);
                  }
                }

                const newData = prevData.map(event => {
                  const eventGroupId = event.id || event.group?.id;
                  if (eventGroupId === groupId && event.participants) {
                    console.log('SSE: Event gefunden, Teilnehmer:', event.participants.length);
                    // Berechne Score aus dem Result
                    const judgeScores = [
                      parseFloat(result.judgeScore1) || 0,
                      parseFloat(result.judgeScore2) || 0,
                      parseFloat(result.judgeScore3) || 0,
                      parseFloat(result.judgeScore4) || 0,
                      parseFloat(result.judgeScore5) || 0
                    ];
                    const penalty = parseFloat(result.penalty) || 0;
                    const validScores = judgeScores.filter(score => score > 0);
                    const averageScore = validScores.length > 0
                      ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
                      : 0;
                    const finalScore = averageScore + penalty;

                    let participantFound = false;
                    const updatedParticipants = event.participants.map(p => {
                      // Prüfe verschiedene ID-Formate: normale ID, partner-team-ID, nation-ID
                      const matches = p.id === participantId ||
                        (result.partnerTeamId && p.id === `partner-team-${result.partnerTeamId}`) ||
                        (p.nationId && `nation-${p.nationId}` === participantId);

                      if (matches) {
                        participantFound = true;
                        console.log('SSE: Teilnehmer gefunden und aktualisiert:', p.id, '->', participantId);

                        // Konvertiere oldScore zu String für korrekten Vergleich
                        const oldScore = typeof p.score === 'string' ? p.score : (p.score !== undefined && p.score !== null ? parseFloat(p.score).toFixed(2) : '0.00');
                        const oldJudgeScores = p.judgeScores || [null, null, null, null, null];
                        const newScore = finalScore.toFixed(2);

                        console.log('SSE: Score-Vergleich für Teilnehmer', p.id, 'oldScore:', oldScore, 'newScore:', newScore, 'gleich?', oldScore === newScore);

                        // Markiere geänderte Werte für Animation
                        const changedKeys = new Set();
                        if (oldScore !== newScore) {
                          changedKeys.add(`score-${participantId}-${event.id}`);
                          console.log('SSE: Score-Änderung erkannt für Teilnehmer', p.id, oldScore, '->', newScore);
                        }
                        judgeScores.forEach((score, idx) => {
                          const oldScore = oldJudgeScores[idx];
                          if (oldScore === undefined || oldScore === null || oldScore !== score) {
                            changedKeys.add(`judgeScore${idx + 1}-${participantId}-${event.id}`);
                          }
                        });
                        if (p.penalty !== penalty) {
                          changedKeys.add(`penalty-${participantId}-${event.id}`);
                        }
                        if (p.rank !== (result.rank || null)) {
                          changedKeys.add(`rank-${participantId}-${event.id}`);
                        }

                        if (changedKeys.size > 0) {
                          setUpdatedValues(prev => {
                            const newSet = new Set(prev);
                            changedKeys.forEach(key => newSet.add(key));
                            setTimeout(() => {
                              setUpdatedValues(current => {
                                const updated = new Set(current);
                                changedKeys.forEach(key => updated.delete(key));
                                return updated;
                              });
                            }, 500);
                            return newSet;
                          });
                        }

                        const updatedParticipant = {
                          ...p,
                          judgeScores,
                          penalty,
                          score: finalScore.toFixed(2),
                          rank: result.rank || null,
                          didNotStart: result.didNotStart !== undefined ? result.didNotStart : p.didNotStart || false,
                          isLate: result.isLate !== undefined ? result.isLate : p.isLate || false
                        };
                        console.log('SSE: Aktualisierter Teilnehmer:', updatedParticipant.id, 'Score:', updatedParticipant.score, 'Rank:', updatedParticipant.rank);
                        return updatedParticipant;
                      }
                      return p;
                    });

                    console.log('SSE: Aktualisierte Teilnehmer für Event', groupId, 'Anzahl:', updatedParticipants.length);
                    if (updatedParticipants.length > 0) {
                      const participantWithScore = updatedParticipants.find(p => parseFloat(p.score) > 0);
                      if (participantWithScore) {
                        console.log('SSE: Teilnehmer mit Score > 0:', participantWithScore.id, 'Score:', participantWithScore.score);
                      }
                    }

                    // Wenn Teilnehmer nicht gefunden wurde, füge ihn mit den korrekten Werten hinzu
                    if (!participantFound) {
                      console.warn('SSE: Teilnehmer NICHT gefunden! Teilnehmer-ID:', participantId, 'Verfügbare IDs:', event.participants.map(p => p.id));
                      console.log('SSE: Versuche Teilnehmer aus Result-Objekt zu erstellen');

                      // Versuche Teilnehmer aus Result-Objekt zu erstellen
                      let newParticipant = null;

                      if (result.partnerTeamId && result.partnerTeam) {
                        // Partner-Team
                        newParticipant = {
                          id: `partner-team-${result.partnerTeamId}`,
                          name: `${result.partnerTeam.participant1?.name || 'Unbekannt'} & ${result.partnerTeam.participant2?.name || 'Unbekannt'}`,
                          participant1: result.partnerTeam.participant1,
                          participant2: result.partnerTeam.participant2,
                          nation: result.partnerTeam.participant1?.nation?.name || 'Unbekannt',
                          nationCode: result.partnerTeam.participant1?.nation?.code || 'DE',
                          isPartnerTeam: true,
                          judgeScores,
                          penalty,
                          score: finalScore.toFixed(2),
                          rank: result.rank || null,
                          didNotStart: result.didNotStart || false,
                          isLate: result.isLate || false
                        };
                      } else if (result.participant) {
                        // Normaler Teilnehmer
                        newParticipant = {
                          id: result.participant.id,
                          name: result.participant.name,
                          nation: result.participant.nation?.name || 'Unbekannt',
                          nationCode: result.participant.nation?.code || 'DE',
                          judgeScores,
                          penalty,
                          score: finalScore.toFixed(2),
                          rank: result.rank || null,
                          didNotStart: result.didNotStart || false,
                          isLate: result.isLate || false
                        };
                      } else if (result.nationId && result.nation) {
                        // Team-Event
                        newParticipant = {
                          id: `nation-${result.nationId}`,
                          name: result.nation.name,
                          nation: result.nation.name,
                          nationCode: result.nation.code,
                          nationId: result.nationId,
                          isTeam: true,
                          judgeScores,
                          penalty,
                          score: finalScore.toFixed(2),
                          rank: result.rank || null,
                          didNotStart: false,
                          isLate: false
                        };
                      }

                      if (newParticipant) {
                        console.log('SSE: Neuer Teilnehmer hinzugefügt:', newParticipant.id, 'Score:', newParticipant.score);
                        updatedParticipants.push(newParticipant);
                      } else {
                        console.warn('SSE: Konnte Teilnehmer nicht aus Result-Objekt erstellen');
                      }
                    }

                    return {
                      ...event,
                      participants: updatedParticipants
                    };
                  }
                  return event;
                });

                console.log('SSE: Neue resultsData:', newData.length, 'Events');
                // Prüfe ob ein Event aktualisiert wurde
                const updatedEvent = newData.find(e => {
                  const eventGroupId = e.id || e.group?.id;
                  return eventGroupId === groupId;
                });
                if (updatedEvent) {
                  const updatedParticipant = updatedEvent.participants.find(p =>
                    p.id === participantId || (p.nationId && `nation-${p.nationId}` === participantId)
                  );
                  if (updatedParticipant) {
                    console.log('SSE: Bestätigung - Teilnehmer aktualisiert:', updatedParticipant.id, 'Score:', updatedParticipant.score);
                  }
                }
                return newData;
              });
            } else {
              console.warn('SSE: Keine participantId gefunden für Update:', result);
            }
          } else if (update.type === 'status-update') {
            // Status-Update verarbeiten (z.B. wenn Gruppe abgeschlossen wird)
            console.log('SSE: Status-Update empfangen für Gruppe', groupId, 'neuer Status:', update.status);

            const newStatus = update.status;

            // Aktualisiere scheduleData
            setScheduleData(prevData => {
              return prevData.map(event => {
                const eventGroupId = event.group?.id || event.id;
                if (eventGroupId === groupId) {
                  const oldStatus = event.status;
                  if (oldStatus !== newStatus) {
                    console.log('SSE: Status-Update - Event', groupId, 'Status:', oldStatus, '->', newStatus);
                    // Markiere Status-Änderung für Animation
                    setUpdatedValues(prev => {
                      const newSet = new Set(prev);
                      newSet.add(`status-${groupId}`);
                      setTimeout(() => {
                        setUpdatedValues(current => {
                          const updated = new Set(current);
                          updated.delete(`status-${groupId}`);
                          return updated;
                        });
                      }, 2000); // Animation für 2 Sekunden
                      return newSet;
                    });
                    return {
                      ...event,
                      status: newStatus
                    };
                  }
                }
                return event;
              });
            });

            // Aktualisiere resultsData
            setResultsData(prevData => {
              return prevData.map(event => {
                const eventGroupId = event.id || event.group?.id;
                if (eventGroupId === groupId) {
                  const oldStatus = event.status;
                  if (oldStatus !== newStatus) {
                    console.log('SSE: Status-Update - Result Event', groupId, 'Status:', oldStatus, '->', newStatus);
                    return {
                      ...event,
                      status: newStatus
                    };
                  }
                }
                return event;
              });
            });

            // Wenn Status auf "Abgeschlossen" geändert wird, lade alle Ergebnisse neu
            if (newStatus === 'Abgeschlossen') {
              console.log('SSE: Gruppe abgeschlossen, lade alle Ergebnisse neu');
              loadAllResultsData();
            }

            return;
          }

          if (update.type === 'rank-update') {
            // Rang-Updates für alle Teilnehmer verarbeiten
            console.log('SSE: Rank-Update empfangen für Gruppe', groupId, 'mit', update.results?.length || 0, 'Ergebnissen');
            console.log('SSE: Rank-Update: Erste 2 Ergebnisse:', update.results?.slice(0, 2));
            setResultsData(prevData => {
              console.log('SSE: Rank-Update: Aktualisiere resultsData, vorher:', prevData.length, 'Events');
              // Prüfe ob Event existiert, wenn nicht, erstelle es
              let eventExists = prevData.some(e => {
                const eventGroupId = e.id || e.group?.id;
                return eventGroupId === groupId;
              });

              if (!eventExists) {
                const scheduleEvent = scheduleData.find(e => {
                  const eventGroupId = e.group?.id || e.id;
                  return eventGroupId === groupId && e.status === 'Aktiv';
                });

                if (scheduleEvent) {
                  console.log('SSE: Rank-Update: Event nicht in resultsData gefunden, erstelle aus scheduleData');
                  const eventParticipants = getParticipantsForEvent(scheduleEvent);
                  const newEvent = {
                    id: groupId,
                    group: scheduleEvent.group,
                    status: 'Aktiv',
                    venue: scheduleEvent.venue,
                    participants: eventParticipants.map(p => {
                      const pGroup = p.groups?.find(g => g.id === groupId);
                      return {
                        id: p.id,
                        name: p.name,
                        nation: p.nation?.name || 'Unbekannt',
                        nationCode: p.nation?.code || 'DE',
                        startNumber: pGroup?.startNumber,
                        judgeScores: [0, 0, 0, 0, 0],
                        penalty: 0,
                        score: 0,
                        rank: 0
                      };
                    })
                  };
                  prevData = [...prevData, newEvent];
                  eventExists = true;
                }
              }

              const newData = prevData.map(event => {
                const eventGroupId = event.id || event.group?.id;
                if (eventGroupId === groupId && event.participants) {
                  console.log('SSE: Rank-Update: Event gefunden, Teilnehmer:', event.participants.length);
                  const updatedParticipants = event.participants.map(p => {
                    const result = update.results.find(r =>
                      (r.participantId && r.participantId === p.id) ||
                      (r.nationId && `nation-${r.nationId}` === `nation-${p.nationId}`)
                    );

                    if (result) {
                      // Aktualisiere auch Scores, falls vorhanden (rank-update enthält alle Ergebnisdaten)
                      const judgeScores = [
                        parseFloat(result.judgeScore1) || 0,
                        parseFloat(result.judgeScore2) || 0,
                        parseFloat(result.judgeScore3) || 0,
                        parseFloat(result.judgeScore4) || 0,
                        parseFloat(result.judgeScore5) || 0
                      ];
                      const penalty = parseFloat(result.penalty) || 0;
                      const validScores = judgeScores.filter(score => score > 0);
                      const averageScore = validScores.length > 0
                        ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
                        : 0;
                      const finalScore = averageScore + penalty;

                      const oldRank = p.rank;
                      const newRank = result.rank || null;
                      const oldScore = p.score;
                      const oldJudgeScores = p.judgeScores || [null, null, null, null, null];

                      console.log('SSE: Rank-Update: Teilnehmer', p.id, 'Rang:', oldRank, '->', newRank, 'Score:', oldScore, '->', finalScore.toFixed(2));

                      // Markiere geänderte Werte für Animation
                      const changedKeys = new Set();
                      if (oldRank !== newRank) {
                        changedKeys.add(`rank-${p.id}-${event.id}`);
                      }
                      if (oldScore !== finalScore.toFixed(2)) {
                        changedKeys.add(`score-${p.id}-${event.id}`);
                      }
                      judgeScores.forEach((score, idx) => {
                        const oldScore = oldJudgeScores[idx];
                        if (oldScore === undefined || oldScore === null || oldScore !== score) {
                          changedKeys.add(`judgeScore${idx + 1}-${p.id}-${event.id}`);
                        }
                      });
                      if (p.penalty !== penalty) {
                        changedKeys.add(`penalty-${p.id}-${event.id}`);
                      }

                      if (changedKeys.size > 0) {
                        setUpdatedValues(prev => {
                          const newSet = new Set(prev);
                          changedKeys.forEach(key => newSet.add(key));
                          setTimeout(() => {
                            setUpdatedValues(current => {
                              const updated = new Set(current);
                              changedKeys.forEach(key => updated.delete(key));
                              return updated;
                            });
                          }, 500);
                          return newSet;
                        });
                      }

                      return {
                        ...p,
                        judgeScores,
                        penalty,
                        score: finalScore.toFixed(2),
                        rank: newRank,
                        didNotStart: result.didNotStart !== undefined ? result.didNotStart : p.didNotStart || false,
                        isLate: result.isLate !== undefined ? result.isLate : p.isLate || false
                      };
                    }
                    return p;
                  });

                  return {
                    ...event,
                    participants: updatedParticipants
                  };
                }
                return event;
              });

              console.log('SSE: Rank-Update: Neue resultsData:', newData.length, 'Events');
              // Debug: Zeige Event-Struktur
              const updatedEvent = newData.find(e => {
                const eventGroupId = e.id || e.group?.id;
                return eventGroupId === groupId;
              });
              if (updatedEvent) {
                console.log('SSE: Rank-Update: Aktualisiertes Event:', {
                  id: updatedEvent.id,
                  groupId: updatedEvent.group?.id,
                  participantsCount: updatedEvent.participants?.length,
                  firstParticipant: updatedEvent.participants?.[0] ? {
                    id: updatedEvent.participants[0].id,
                    score: updatedEvent.participants[0].score,
                    rank: updatedEvent.participants[0].rank
                  } : null
                });
              }
              return newData;
            });

            // Aktualisiere letzte Aktualisierungszeit
            setLastUpdateTimes(prev => {
              const newMap = new Map(prev);
              newMap.set(groupId, new Date());
              return newMap;
            });
          }
        };

        // Verbindung herstellen
        console.log(`SSE: Verbinde mit Gruppe ${groupId}`);
        resultsStreamService.connect(groupId, handleUpdate);

        // Cleanup-Funktion speichern
        activeGroupConnectionsRef.current.set(groupId, () => {
          resultsStreamService.disconnectGroup(groupId);
        });
      }
    });

    // Cleanup bei Unmount
    return () => {
      activeGroupConnectionsRef.current.forEach((cleanup) => cleanup());
      activeGroupConnectionsRef.current.clear();
    };
  }, [resultsData, scheduleData, participants]);

  // Hilfsfunktionen für Schedule-ähnliche Anzeige
  const getParticipantsForEvent = (event) => {
    if (!event.group) return [];

    // Spezielle Behandlung für Partner-Teams
    const isPartnerForm = event.group?.category?.type === 'Partnerform';
    const partnerTeamsForGroup = partnerTeamsData.get(event.group?.id);

    if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
      // Sammle alle Teilnehmer aus allen Partner-Teams
      const allPartnerParticipants = [];
      partnerTeamsForGroup.partnerTeams.forEach(team => {
        if (team.participant1) allPartnerParticipants.push(team.participant1);
        if (team.participant2) allPartnerParticipants.push(team.participant2);
      });
      return allPartnerParticipants;
    }

    // Spezielle Behandlung für Team-Events (Gruppenwettbewerb)
    const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');

    if (isTeamEvent && teamData && teamData.teams) {
      // Sammle alle Teilnehmer aus allen Teams
      const allTeamParticipants = [];
      teamData.teams.forEach(team => {
        if (team.participants && Array.isArray(team.participants)) {
          allTeamParticipants.push(...team.participants);
        }
      });
      return allTeamParticipants;
    }

    // Normale Gruppen: Filtere über ParticipantGroup
    if (!participants.length) return [];

    const eventParticipants = participants.filter(participant => {
      if (!participant.groups || !Array.isArray(participant.groups)) {
        return false;
      }

      return participant.groups.some(group => group.id === event.group.id);
    });

    eventParticipants.sort((a, b) => {
      const aGroup = a.groups.find(g => g.id === event.group.id);
      const bGroup = b.groups.find(g => g.id === event.group.id);
      const aStartNumber = aGroup ? aGroup.startNumber : 999;
      const bStartNumber = bGroup ? bGroup.startNumber : 999;
      return aStartNumber - bStartNumber;
    });

    return eventParticipants;
  };

  const calculateGroupDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '0min';

    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  // Funktion zum Rendern der Teilnehmerliste (wie im Schedule)
  const renderParticipantsList = (event) => {
    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
    const eventGroupId = event.group?.id || event.id;
    const isTeamEvent = !!(eventInSchedule?.group?.isTeamCompetition || eventInSchedule?.group?.id === 'team-competition' || eventInSchedule?.group?.name === 'Gruppenwettbewerb' ||
      event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
    const isPartnerForm = eventInSchedule?.group?.category?.type === 'Partnerform' || event.group?.category?.type === 'Partnerform' || event.isPartnerForm;
    const partnerTeamsForGroup = partnerTeamsData.get(eventGroupId) || (event.isPartnerForm && event.partnerTeams ? { partnerTeams: event.partnerTeams } : null);

    // Spezielle Darstellung für Partner-Teams
    if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
      return (
        <div className="mt-4 space-y-4">
          <h5 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-handshake text-slate-500 dark:text-slate-400"></i>
            Partner-Teams ({partnerTeamsForGroup.partnerTeams.length} Teams)
          </h5>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {partnerTeamsForGroup.partnerTeams.map((team) => (
                <div key={team.id} className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {team.startNumber && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                        #{team.startNumber}
                      </span>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        {/* Teilnehmer 1 */}
                        <div className="flex items-center gap-2">
                          {team.participant1?.nation?.code && (
                            <img
                              src={process.env.PUBLIC_URL + "/images/flags/" + team.participant1.nation.code + ".png"}
                              alt={team.participant1.nation.name}
                              className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                            />
                          )}
                          <div>
                            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {team.participant1?.name || 'Unbekannt'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {team.participant1?.participantNumber || team.participant1?.id}
                            </div>
                          </div>
                        </div>

                        {/* Verbindungszeichen */}
                        <div className="text-slate-400 dark:text-slate-500">
                          <i className="fas fa-handshake"></i>
                        </div>

                        {/* Teilnehmer 2 */}
                        <div className="flex items-center gap-2">
                          {team.participant2?.nation?.code && (
                            <img
                              src={process.env.PUBLIC_URL + "/images/flags/" + team.participant2.nation.code + ".png"}
                              alt={team.participant2.nation.name}
                              className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                            />
                          )}
                          <div>
                            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {team.participant2?.name || 'Unbekannt'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {team.participant2?.participantNumber || team.participant2?.id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full">
                      <i className="fas fa-handshake text-[10px]"></i>
                      Partner
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Spezielle Darstellung für Team-Events (Gruppenwettbewerb)
    if (isTeamEvent && teamData && teamData.teams) {
      return (
        <div className="mt-4 space-y-4">
          <h5 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
            Teams ({teamData.teams.length} Nationen, {teamData.currentParticipants || 0} Teilnehmer)
          </h5>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {teamData.teams.map((team) => {
                const isExpanded = expandedNations.has(team.nationId);
                return (
                  <div key={team.nationId} className="transition-colors">
                    {/* Nationen-Header (ausklappbar) */}
                    <button
                      onClick={() => handleToggleNationExpansion(team.nationId)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          <i className="fas fa-chevron-right text-slate-400 dark:text-slate-500 text-sm"></i>
                        </div>
                        {team.startNumber && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                            #{team.startNumber}
                          </span>
                        )}
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + (team.nationCode || 'DE') + ".png"}
                          alt={team.nationName}
                          className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{team.nationName}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{team.teamSize} Teilnehmer</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                          <i className="fas fa-users text-[10px]"></i>
                          Team
                        </span>
                      </div>
                    </button>

                    {/* Ausklappbare Teilnehmer-Liste */}
                    {isExpanded && team.participants && (
                      <div className="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                              <thead className="bg-white dark:bg-slate-800">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">#</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Teilnehmer</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Alter</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Geschlecht</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                                {team.participants.map((participant, index) => (
                                  <tr key={participant.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                                      <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                                        {participant.participantNumber || index + 1}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">{participant.name}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{participant.age || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                      {participant.gender === 'männlich' ? 'M' : participant.gender === 'weiblich' ? 'W' : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    // Normale Teilnehmerliste für reguläre Events
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {event.participants.map((participant) => {
            const nationCode = participant.nation?.code || participant.nationCode || 'DE';
            const nationName = participant.nation?.name || participant.nation || '';

            return (
              <div
                key={participant.id}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                    #{participant.startNumber || participant.participantNumber || participant.id}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">
                      {participant.name || participant.id}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {nationCode && (
                        <>
                          <img
                            src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                            alt={nationName}
                            className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                          />
                          <span className="truncate">{nationName}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('de-DE', options);
  };

  // Berechne feste Nummern pro Event (pro Fläche + Tag), basierend auf scheduleData
  // Diese Nummern ändern sich NICHT durch Filter (gleiche Logik wie im Zeitplan)
  const eventSequenceNumbers = useMemo(() => {
    const sequenceMap = {};
    // Gruppiere Schedule-Events nach Fläche
    const eventsByVenue = {};
    scheduleData.forEach(event => {
      if (!event.venue) return;
      if (!eventsByVenue[event.venue]) eventsByVenue[event.venue] = [];
      eventsByVenue[event.venue].push(event);
    });
    // Pro Fläche: Gruppiere nach Tag und nummeriere
    Object.entries(eventsByVenue).forEach(([venue, events]) => {
      const eventsByDay = {};
      events.forEach(event => {
        const day = event.startTime ? new Date(event.startTime).toDateString() : 'unknown';
        if (!eventsByDay[day]) eventsByDay[day] = [];
        eventsByDay[day].push(event);
      });
      Object.values(eventsByDay).forEach(dayEvents => {
        dayEvents.forEach((event, idx) => {
          // Verwende group.id als Key, da combinedData die groupId als event.id nutzt
          const groupId = event.group?.id || event.id;
          sequenceMap[groupId] = idx + 1;
        });
      });
    });
    return sequenceMap;
  }, [scheduleData]);

  const getStatusLabel = (status) => {
    switch (status) {
      case "Ausstehend":
        return t("admin.schedule.pending", "Ausstehend");
      case "Aktiv":
        return t("admin.schedule.active", "Aktiv");
      case "Abgeschlossen":
        return t("admin.schedule.completed", "Abgeschlossen");
      case "Vorbereitung":
        return t("admin.schedule.preparation", "Vorbereitung");
      default:
        return status;
    }
  };

  const handleToggleEventExpand = async (eventId) => {
    const newExpandedEvents = new Set(expandedEvents);
    const event = scheduleData.find(e => (e.group?.id || e.id) === eventId);
    const isTeamEvent = !!(event?.group?.isTeamCompetition || event?.group?.id === 'team-competition' || event?.group?.name === 'Gruppenwettbewerb');

    if (newExpandedEvents.has(eventId)) {
      newExpandedEvents.delete(eventId);
      // Lösche nur erweiterte Nationen, aber behalte teamData
      setExpandedNations(new Set());
    } else {
      newExpandedEvents.add(eventId);
      // Lade Team-Daten wenn Team-Event erweitert wird (nur wenn noch nicht vorhanden)
      if (isTeamEvent && !teamData) {
        try {
          const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
          if (teamGroupResponse.success && teamGroupResponse.data) {
            setTeamData(teamGroupResponse.data);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Team-Daten:', error);
        }
      }
    }
    setExpandedEvents(newExpandedEvents);
  };

  const handleToggleNationExpansion = (nationId) => {
    const newExpandedNations = new Set(expandedNations);
    if (newExpandedNations.has(nationId)) {
      newExpandedNations.delete(nationId);
    } else {
      newExpandedNations.add(nationId);
    }
    setExpandedNations(newExpandedNations);
  };

  const handleToggleResultsExpand = (eventId) => {
    const newExpandedResults = new Set(expandedResults);
    if (newExpandedResults.has(eventId)) {
      newExpandedResults.delete(eventId);
    } else {
      newExpandedResults.add(eventId);
    }
    setExpandedResults(newExpandedResults);
  };

  // Handler für "Alle Ergebnisse ausklappen/einklappen"
  const handleExpandAllResults = () => {
    const allEventIds = filteredData.map(event => event.id);
    setExpandedResults(new Set(allEventIds));
  };

  const handleCollapseAllResults = () => {
    setExpandedResults(new Set());
  };

  // Handler für globale "Detail-Punkte anzeigen" Option
  const handleToggleShowAllJudgeScores = (checked) => {
    setShowAllJudgeScores(checked);
    if (checked) {
      // Wenn aktiviert, klappe alle Wertungsrichter-Punkte aus
      const allParticipantIds = new Set();
      filteredData.forEach(event => {
        if (event.participants) {
          event.participants.forEach(p => allParticipantIds.add(p.id));
        }
      });
      setExpandedJudgeScores(allParticipantIds);
    } else {
      // Wenn deaktiviert, klappe alle ein
      setExpandedJudgeScores(new Set());
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSelectedAgeGroup("all");
    setSelectedGender("all");
    setSelectedLocation("all");
    setSelectedViewMode("all");
    if (dayOptions.length > 0) {
      setSelectedDay(dayOptions[0].value);
    }
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedCategory !== "all") {
      const categoryLabel = categoryOptions.find(c => c.value === selectedCategory)?.label || selectedCategory;
      active.push({ type: "Kategorie", value: categoryLabel, onRemove: () => setSelectedCategory("all") });
    }
    if (selectedAgeGroup !== "all") {
      const ageGroupLabel = ageGroupOptions.find(a => a.value === selectedAgeGroup)?.label || selectedAgeGroup;
      active.push({ type: "Altersgruppe", value: ageGroupLabel, onRemove: () => setSelectedAgeGroup("all") });
    }
    if (selectedGender !== "all") {
      const genderLabel = genderOptions.find(g => g.value === selectedGender)?.label || selectedGender;
      active.push({ type: "Geschlecht", value: genderLabel, onRemove: () => setSelectedGender("all") });
    }
    if (selectedLocation !== "all") {
      const locationLabel = locationOptions.find(l => l.value === selectedLocation)?.label || selectedLocation;
      active.push({ type: "Fläche", value: locationLabel, onRemove: () => setSelectedLocation("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };


  const handleToggleJudgeScores = (participantId) => {
    const newExpandedJudgeScores = new Set(expandedJudgeScores);
    if (newExpandedJudgeScores.has(participantId)) {
      newExpandedJudgeScores.delete(participantId);
    } else {
      newExpandedJudgeScores.add(participantId);
    }
    setExpandedJudgeScores(newExpandedJudgeScores);
  };

  const handleToggleAllJudgeScores = (eventId) => {
    const event = combinedData.find(e => e.id === eventId);
    if (!event) return;

    const eventParticipantIds = event.participants.map(p => p.id);
    const allExpanded = eventParticipantIds.every(id => expandedJudgeScores.has(id));

    const newExpandedJudgeScores = new Set(expandedJudgeScores);

    if (allExpanded) {
      eventParticipantIds.forEach(id => newExpandedJudgeScores.delete(id));
    } else {
      eventParticipantIds.forEach(id => newExpandedJudgeScores.add(id));
    }

    setExpandedJudgeScores(newExpandedJudgeScores);
  };


  const categoryOptions = [
    { value: "all", label: t("admin.results.allCategories", "Alle Kategorien") },
    ...categories.map((category) => ({
      value: category.name || category,
      label: category.name || category
    }))
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.results.allAgeGroups", "Alle Altersgruppen") },
    ...ageGroups
      .filter((group) => group.name !== 'Alle') // "Alle" ausschließen
      .map((group) => ({
        value: group.name,
        label: group.name
      }))
  ];

  const genderOptions = [
    { value: "all", label: t("admin.results.allGenders", "Alle Geschlechter") },
    { value: "männlich", label: "Männlich" },
    { value: "weiblich", label: "Weiblich" },
    { value: "gemischt", label: "Gemischt" }
  ];

  const locationOptions = [
    { value: "all", label: t("admin.results.allLocations", "Alle Flächen") },
    { value: "Fläche A", label: "Fläche A" },
    { value: "Fläche B", label: "Fläche B" }
  ];

  const dayOptions = useMemo(() => {
    if (!scheduleData.length) return [];

    // Extrahiere alle eindeutigen Tage aus scheduleData
    const days = new Set();
    scheduleData.forEach(item => {
      if (item.startTime) {
        const date = item.startTime.split('T')[0];
        days.add(date);
      }
    });

    const sortedDays = Array.from(days).sort();

    return sortedDays.map(day => {
      const date = new Date(day);
      const formattedDate = date.toLocaleDateString('de-DE', {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      return {
        value: day,
        label: formattedDate
      };
    });
  }, [scheduleData]);

  // Setze ersten Tag als Standard, wenn noch keiner ausgewählt
  useEffect(() => {
    if (!selectedDay && dayOptions.length > 0) {
      setSelectedDay(dayOptions[0].value);
    }
  }, [dayOptions, selectedDay]);

  const handleDayChange = (day) => {
    setSelectedDay(day);
  };

  // Kombiniere Schedule- und Results-Daten basierend auf Status
  const combinedData = useMemo(() => {
    console.log('combinedData: Neuberechnung, resultsData:', resultsData.length, 'allResultsData:', allResultsData.length, 'scheduleData:', scheduleData.length);
    // Debug: Zeige erste Ergebnisse aus resultsData
    if (resultsData.length > 0) {
      const firstResult = resultsData[0];
      console.log('combinedData: Erstes Event in resultsData:', firstResult.id || firstResult.group?.id, 'Teilnehmer:', firstResult.participants?.length);
      if (firstResult.participants && firstResult.participants.length > 0) {
        console.log('combinedData: Erster Teilnehmer:', firstResult.participants[0].id, 'Score:', firstResult.participants[0].score, 'Rank:', firstResult.participants[0].rank);
      }
    }
    const combined = [];

    // Erstelle zwei Maps: eine für aktive Events (resultsData mit SSE-Updates) und eine für abgeschlossene Events (allResultsData)
    const activeResultsMap = new Map();
    resultsData.forEach(result => {
      const eventId = result.id || result.group?.id;
      if (eventId) {
        activeResultsMap.set(eventId, result);
      }
    });

    const completedResultsMap = new Map();
    allResultsData.forEach(result => {
      const eventId = result.id || result.group?.id;
      if (eventId) {
        completedResultsMap.set(eventId, result);
      }
    });

    console.log('combinedData: activeResultsMap:', activeResultsMap.size, 'completedResultsMap:', completedResultsMap.size);

    scheduleData.forEach(event => {
      const groupId = event.group?.id || event.id;
      // Für aktive Events: Verwende resultsData (wird durch SSE aktualisiert)
      // Für abgeschlossene Events: Verwende allResultsData (statische Daten)
      const existingResult = event.status === "Aktiv"
        ? activeResultsMap.get(groupId)
        : completedResultsMap.get(groupId) || activeResultsMap.get(groupId);
      console.log('combinedData: Verarbeite Event', groupId, 'Status:', event.status, 'existingResult:', !!existingResult, 'existingResult participants:', existingResult?.participants?.length);
      if (existingResult && existingResult.participants) {
        console.log('combinedData: existingResult Teilnehmer-IDs:', existingResult.participants.map(p => p.id).slice(0, 5));
      }

      if (event.status === "Ausstehend" || event.status === "Vorbereitung") {
        // Prüfe ob es eine Partnerform ist
        const isPartnerForm = event.group?.category?.type === 'Partnerform';
        const partnerTeamsForGroup = partnerTeamsData.get(event.group?.id);

        let eventParticipants;
        if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
          // Für Partner-Teams: Erstelle Einträge für jedes Team
          eventParticipants = partnerTeamsForGroup.partnerTeams.map(team => ({
            id: `partner-team-${team.id}`,
            name: `${team.participant1?.name || 'Unbekannt'} & ${team.participant2?.name || 'Unbekannt'}`,
            participant1: team.participant1,
            participant2: team.participant2,
            nation: team.participant1?.nation?.name || 'Unbekannt',
            nationCode: team.participant1?.nation?.code || 'DE',
            startNumber: team.startNumber,
            judgeScores: [0, 0, 0, 0, 0],
            penalty: 0,
            score: 0,
            rank: 0,
            isPartnerTeam: true
          }));
        } else {
          // Normale Events: Einzelne Teilnehmer
          eventParticipants = getParticipantsForEvent(event).map(participant => {
            // Startnummer aus der Gruppen-Zuordnung ermitteln
            const pGroup = participant.groups?.find(g => g.id === groupId);
            return {
              id: participant.id,
              name: participant.name,
              nation: participant.nation?.name || 'Unbekannt',
              nationCode: participant.nation?.code || 'DE',
              startNumber: pGroup?.startNumber,
              judgeScores: [0, 0, 0, 0, 0],
              penalty: 0,
              score: 0,
              rank: 0
            };
          });
        }

        combined.push({
          id: groupId,
          type: 'schedule',
          eventName: event.group?.name || 'Unbekanntes Event',
          location: event.venue,
          category: event.group?.category?.name,
          ageGroup: event.group?.ageGroup?.name,
          gender: event.group?.gender,
          status: event.status,
          startTime: event.startTime,
          endTime: event.endTime,
          group: event.group,
          participants: eventParticipants,
          isPartnerForm: isPartnerForm,
          partnerTeams: isPartnerForm && partnerTeamsForGroup ? partnerTeamsForGroup.partnerTeams : undefined
        });
      } else if (event.status === "Aktiv" || event.status === "Abgeschlossen") {
        // Prüfe ob es ein Team-Event oder Partnerform ist
        const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
        const isPartnerForm = event.group?.category?.type === 'Partnerform';
        const partnerTeamsForGroup = partnerTeamsData.get(event.group?.id);

        let baseResults;
        if (isTeamEvent && teamData && teamData.teams) {
          // Für Team-Events: Gruppiere nach Nationen
          baseResults = teamData.teams.map(team => ({
            id: `nation-${team.nationId}`, // Eindeutige ID für Nation
            name: team.nationName,
            nation: team.nationName,
            nationCode: team.nationCode || 'DE',
            nationId: team.nationId,
            teamSize: team.teamSize,
            judgeScores: [0, 0, 0, 0, 0],
            penalty: 0,
            score: 0,
            rank: 0,
            isTeam: true
          }));
        } else if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
          // Für Partner-Teams: Jedes Team als Eintrag
          baseResults = partnerTeamsForGroup.partnerTeams.map(team => ({
            id: `partner-team-${team.id}`,
            name: `${team.participant1?.name || 'Unbekannt'} & ${team.participant2?.name || 'Unbekannt'}`,
            participant1: team.participant1,
            participant2: team.participant2,
            nation: team.participant1?.nation?.name || 'Unbekannt',
            nationCode: team.participant1?.nation?.code || 'DE',
            startNumber: team.startNumber,
            judgeScores: [0, 0, 0, 0, 0],
            penalty: 0,
            score: 0,
            rank: 0,
            isPartnerTeam: true
          }));
        } else {
          // Normale Events: Einzelne Teilnehmer
          baseResults = getParticipantsForEvent(event).map(participant => {
            // Startnummer aus der Gruppen-Zuordnung ermitteln
            const pGroup = participant.groups?.find(g => g.id === groupId);
            return {
              id: participant.id,
              name: participant.name,
              nation: participant.nation?.name || 'Unbekannt',
              nationCode: participant.nation?.code || 'DE',
              startNumber: pGroup?.startNumber,
              judgeScores: [0, 0, 0, 0, 0],
              penalty: 0,
              score: '0.00', // Als String initialisieren für Konsistenz mit resultsData
              rank: 0
            };
          });
        }

        console.log('combinedData: baseResults Teilnehmer-IDs:', baseResults.map(p => p.id).slice(0, 5));

        // Übernehme Werte aus resultsData, falls vorhanden (für Live-Updates)
        if (existingResult && existingResult.participants) {
          console.log('combinedData: Übernehme Werte aus existingResult für Gruppe', groupId, 'mit', existingResult.participants.length, 'Teilnehmern');
          const resultsMap = new Map();
          existingResult.participants.forEach(p => {
            resultsMap.set(p.id, p);
            console.log('combinedData: resultsMap - ID:', p.id, 'Score:', p.score, 'Rank:', p.rank, 'DNS:', p.didNotStart);
          });

          baseResults.forEach(participant => {
            const resultParticipant = resultsMap.get(participant.id);
            if (resultParticipant) {
              console.log('combinedData: Aktualisiere Teilnehmer', participant.id, 'Score:', participant.score, '->', resultParticipant.score, 'JudgeScores:', resultParticipant.judgeScores, 'Type:', typeof resultParticipant.score, 'DNS:', resultParticipant.didNotStart);
              // Übernehme alle Werte, auch wenn sie 0 sind (wichtig für Live-Updates)
              participant.judgeScores = Array.isArray(resultParticipant.judgeScores)
                ? resultParticipant.judgeScores
                : [0, 0, 0, 0, 0];
              participant.penalty = resultParticipant.penalty !== undefined && resultParticipant.penalty !== null ? resultParticipant.penalty : 0;
              // Score kann String oder Number sein, konvertiere zu String für Konsistenz
              // WICHTIG: Auch wenn score 0 ist (Number) oder '0.00' (String), muss es übernommen werden
              // WICHTIG: IMMER den Score aus resultParticipant übernehmen, auch wenn er '0.00' ist
              // Das ist wichtig, damit Updates funktionieren, wenn ein Athlet zum ersten Mal Punkte bekommt
              const oldScoreValue = participant.score;
              if (resultParticipant.score !== undefined && resultParticipant.score !== null) {
                if (typeof resultParticipant.score === 'string') {
                  participant.score = resultParticipant.score;
                } else {
                  // Number: konvertiere zu String mit 2 Dezimalstellen
                  participant.score = parseFloat(resultParticipant.score).toFixed(2);
                }
                console.log('combinedData: Score übernommen für Teilnehmer', participant.id, 'von', oldScoreValue, 'zu', participant.score);
              } else {
                // Wenn score undefined/null ist, behalte den aktuellen Wert oder setze auf '0.00'
                // Aber nur wenn er noch nicht gesetzt wurde
                if (participant.score === undefined || participant.score === null || participant.score === 0) {
                  participant.score = '0.00';
                }
                console.log('combinedData: Score nicht verfügbar für Teilnehmer', participant.id, 'behalte:', participant.score);
              }
              participant.rank = resultParticipant.rank !== undefined && resultParticipant.rank !== null ? resultParticipant.rank : 0;
              participant.didNotStart = resultParticipant.didNotStart !== undefined ? resultParticipant.didNotStart : false;
              participant.isLate = resultParticipant.isLate !== undefined ? resultParticipant.isLate : false;
              console.log('combinedData: Nach Update - Score:', participant.score, 'Rank:', participant.rank, 'DNS:', participant.didNotStart, 'Type:', typeof participant.score);
            } else {
              console.log('combinedData: Teilnehmer', participant.id, 'nicht in existingResult gefunden. Verfügbare IDs:', Array.from(resultsMap.keys()));
            }
          });
        } else {
          console.log('combinedData: Kein existingResult für Gruppe', groupId, 'existingResult:', existingResult);
        }


        combined.push({
          id: groupId,
          type: 'result', // WICHTIG: Immer 'result' für Aktiv/Abgeschlossen damit Ergebnisse angezeigt werden
          eventName: event.group?.name || 'Unbekanntes Event',
          location: event.venue,
          category: event.group?.category?.name,
          ageGroup: event.group?.ageGroup?.name,
          gender: event.group?.gender,
          status: event.status,
          startTime: event.startTime,
          endTime: event.endTime,
          participants: baseResults,
          isTeamEvent: isTeamEvent,
          isPartnerForm: isPartnerForm
        });
      }
    });

    // Sortiere combinedData nach sequenceNumber aus scheduleData (Zeitplan-Reihenfolge)
    const scheduleMap = new Map();
    scheduleData.forEach(event => {
      const groupId = event.group?.id || event.id;
      scheduleMap.set(groupId, event);
    });

    combined.sort((a, b) => {
      // Prio für Ergebnisse
      const aEvent = scheduleMap.get(a.id);
      const bEvent = scheduleMap.get(b.id);

      if (!aEvent || !bEvent) {
        // Wenn ein Event nicht im Schedule ist, sortiere nach Name
        if (!aEvent && !bEvent) return a.eventName.localeCompare(b.eventName);
        if (!aEvent) return 1;
        if (!bEvent) return -1;
      }

      const aSequence = aEvent?.sequenceNumber ?? 999999;
      const bSequence = bEvent?.sequenceNumber ?? 999999;

      return aSequence - bSequence;
    });

    return combined;
  }, [scheduleData, resultsData, participants, teamData, allResultsData, partnerTeamsData]);

  // Debug: Reagiere auf Änderungen von resultsData
  useEffect(() => {
    console.log('resultsData geändert:', resultsData.length, 'Events');
    resultsData.forEach(event => {
      const eventGroupId = event.id || event.group?.id;
      if (event.participants && event.participants.length > 0) {
        console.log('Event', eventGroupId, 'hat', event.participants.length, 'Teilnehmer');
        event.participants.slice(0, 3).forEach(p => {
          console.log('  Teilnehmer', p.id, 'Score:', p.score, 'Rank:', p.rank);
        });
      }
    });
  }, [resultsData]);


  // Filtere kombinierte Daten
  const filteredData = combinedData.filter((item) => {
    const matchesSearch = item.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.participants.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Prüfe ob es ein Team-Event ist (überprüfe sowohl combinedData item als auch scheduleData)
    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === item.id);
    const isTeamEvent = !!(item.isTeamCompetition || item.groupId === 'team-competition' ||
      eventInSchedule?.group?.name === 'Gruppenwettbewerb' ||
      eventInSchedule?.group?.isTeamCompetition ||
      eventInSchedule?.group?.id === 'team-competition');

    let matchesCategory, matchesAgeGroup, matchesGender;

    if (isTeamEvent) {
      // Spezielle Filterlogik für Team-Events - verwende die Kategorie "Gruppenperformance"
      matchesCategory = selectedCategory === "all" || selectedCategory === "Gruppenperformance";
      matchesAgeGroup = selectedAgeGroup === "all" || item.ageGroup === selectedAgeGroup || item.ageGroup === 'Alle';
      matchesGender = selectedGender === "all" || item.gender === selectedGender || (item.gender === 'gemischt' && selectedGender === 'gemischt');
    } else {
      // Normale Filterlogik
      matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
      matchesAgeGroup = selectedAgeGroup === "all" || item.ageGroup === selectedAgeGroup;
      matchesGender = selectedGender === "all" || (item.gender && String(item.gender).toLowerCase() === selectedGender);
    }

    const matchesLocation = selectedLocation === "all" || item.location === selectedLocation;

    // Day Filter
    const matchesDay = !selectedDay || (item.startTime && item.startTime.startsWith(selectedDay));

    // View Mode Filter
    // Bei "Zeitplan": Zeige schedule-Items UND aktive Events (die haben type "result" aber sind noch live)
    const matchesViewMode = selectedViewMode === "all" ||
      (selectedViewMode === "schedule" && (item.type === "schedule" || item.status === "Aktiv")) ||
      (selectedViewMode === "results" && item.type === "result");

    return matchesSearch && matchesCategory && matchesAgeGroup && matchesGender && matchesLocation && matchesDay && matchesViewMode;
  });

  // Gruppiere Daten nach Fläche und sortiere nach ursprünglicher Position
  const dataByLocation = {};

  // Erstelle eine Map für schnelle Zuordnung von groupId zu sequenceNumber
  const sequenceMap = new Map();
  scheduleData.forEach(event => {
    const groupId = event.group?.id || event.id;
    sequenceMap.set(groupId, event.sequenceNumber ?? 999999);
  });

  const sortBySchedulePosition = (a, b) => {
    // Bei "Nur Zeitplan" Ansicht: Aktive Gruppen zuerst anzeigen
    if (selectedViewMode === "schedule") {
      const aActive = a.status === 'Aktiv' ? 0 : a.status === 'Vorbereitung' ? 1 : 2;
      const bActive = b.status === 'Aktiv' ? 0 : b.status === 'Vorbereitung' ? 1 : 2;
      if (aActive !== bActive) return aActive - bActive;
    }

    const aSequence = sequenceMap.get(a.id) ?? 999999;
    const bSequence = sequenceMap.get(b.id) ?? 999999;
    return aSequence - bSequence;
  };

  if (selectedLocation === "all") {
    const halle1Events = filteredData.filter(item => item.location === "Fläche A");
    const halle2Events = filteredData.filter(item => item.location === "Fläche B");

    dataByLocation["Fläche A"] = halle1Events.sort(sortBySchedulePosition);
    dataByLocation["Fläche B"] = halle2Events.sort(sortBySchedulePosition);
  } else {
    const locationEvents = filteredData.filter(item => item.location === selectedLocation);

    dataByLocation[selectedLocation] = locationEvents.sort(sortBySchedulePosition);
  }

  // Berechne Gesamtanzahl der Teilnehmer pro Fläche
  const getTotalParticipants = (events) => {
    return events.reduce((total, event) => {
      const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
      const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' ||
        eventInSchedule?.group?.isTeamCompetition ||
        eventInSchedule?.group?.id === 'team-competition');
      const isPartnerForm = eventInSchedule?.group?.category?.type === 'Partnerform';
      const partnerTeamsForGroup = partnerTeamsData.get(eventInSchedule?.group?.id);

      if (isTeamEvent && teamData && teamData.currentParticipants) {
        return total + teamData.currentParticipants;
      }
      if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
        return total + partnerTeamsForGroup.partnerTeams.length;
      }
      return total + event.participants.length;
    }, 0);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return `${rank}.`;
    }
  };


  const addToast = (type, message) => setToast({ show: true, message, type });
  const hideToast = () => setToast({ show: false, message: "", type: "info" });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
            {/* Header Skeleton */}
            <div className="mb-6 flex items-center gap-3">
              <div className="h-7 bg-slate-200 dark:bg-slate-700 rounded-lg w-48"></div>
              <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-14"></div>
            </div>

            {/* Filter Section Skeleton */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
              {/* Search */}
              <div className="mb-5 h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              {/* Day Chips */}
              <div className="mb-5">
                <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-2"></div>
                <div className="flex gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                  ))}
                </div>
              </div>
              {/* Filter Dropdowns */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                {[...Array(4)].map((_, i) => (
                  <div key={i}>
                    <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-2"></div>
                    <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                  </div>
                ))}
              </div>
              {/* Ansicht Controls */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-14 mb-2"></div>
                <div className="flex gap-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Cards Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(2)].map((_, li) => (
                <div key={li} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                  {/* Venue Header */}
                  <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60 flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-xl flex-shrink-0"></div>
                    <div>
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-1.5"></div>
                      <div className="flex gap-2">
                        <div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                        <div className="h-4 w-18 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  {/* Events */}
                  <div className="p-4 space-y-4">
                    {[...Array(3)].map((_, ei) => (
                      <div key={ei} className="bg-white dark:bg-slate-800 border border-l-4 border-slate-200 dark:border-slate-700 border-l-slate-300 dark:border-l-slate-600 rounded-2xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="h-5 w-5 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                            <div className="h-5 w-20 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                            <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded"></div>
                          </div>
                          <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <div className="h-2.5 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-1.5"></div>
                            <div className="h-5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                          </div>
                          <div className="border-t border-slate-100 dark:border-slate-700/60 pt-2.5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
                              <div className="space-y-1">
                                <div className="h-2 w-14 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded"></div>
                              </div>
                            </div>
                            <div className="h-8 w-20 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0.7; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-20px); opacity: 0.7; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1); will-change: transform, opacity; }
        .animate-slide-down { animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1); will-change: transform, opacity; }
      `}</style>
      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                    {t("admin.results.title", "Ergebnisse")}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20">
                    <i className="fas fa-trophy text-[10px]"></i>
                    {filteredData.length}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t("admin.results.subtitle", "Wettkampfergebnisse und Rankings")}
                </p>
              </div>
            </div>
          </div>


          {/* Filter Section */}
          <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
            {/* Search Bar */}
            <div className="mb-5">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("admin.results.search", "Event oder Teilnehmer suchen…")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-9 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs"></i>
              </div>
            </div>

            {/* Day Filter - Chips */}
            <div className="mb-5">
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.results.selectDay", "Tag auswählen")}
              </label>
              <div className="flex flex-wrap gap-2">
                {dayOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`inline-flex items-center gap-1.5 h-8 px-3.5 text-xs font-medium rounded-xl transition-colors ${selectedDay === option.value
                      ? 'bg-primary dark:bg-primary-light text-white border border-primary dark:border-primary-light'
                      : 'bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-primary/50 dark:hover:border-primary-light/50'
                      }`}
                    onClick={() => handleDayChange(option.value)}
                  >
                    <i className="fas fa-calendar text-[10px]"></i>
                    {option.label}
                  </button>
                ))}
              </div>
              {!selectedDay && dayOptions.length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  Bitte einen Tag auswählen
                </p>
              )}
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {/* Category Filter */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.results.category", "Kategorie")}
                </label>
                <GenericSelectTailwind
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  options={categoryOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  placeholder="Kategorie auswählen..."
                />
              </div>

              {/* Age Group Filter */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.results.ageGroup", "Altersgruppe")}
                </label>
                <GenericSelectTailwind
                  value={selectedAgeGroup}
                  onChange={handleAgeGroupChange}
                  options={ageGroupOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  placeholder="Altersgruppe auswählen..."
                />
              </div>

              {/* Gender Filter */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.results.gender", "Geschlecht")}
                </label>
                <GenericSelectTailwind
                  value={selectedGender}
                  onChange={handleGenderChange}
                  options={genderOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                    icon:
                      opt.value === "männlich" ? <i className="fas fa-mars text-blue-600" /> :
                        opt.value === "weiblich" ? <i className="fas fa-venus text-pink-600" /> :
                          opt.value === "gemischt" ? <i className="fas fa-venus-mars text-purple-500" /> :
                            null
                  }))}
                  leftIcon={<i className="fas fa-venus-mars text-slate-400 dark:text-slate-500" />}
                  placeholder="Geschlecht auswählen..."
                />
              </div>

              {/* Location Filter */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  {t("admin.results.location", "Fläche")}
                </label>
                <GenericSelectTailwind
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  options={locationOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  placeholder="Fläche auswählen..."
                />
              </div>
            </div>

            {/* Active Filters Section */}
            {getActiveFilters().length > 0 && (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    Aktive Filter
                  </span>
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                  >
                    <i className="fas fa-undo text-[10px]"></i>
                    Zurücksetzen
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {getActiveFilters().map((filter, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                    >
                      <span>{filter.type}: {filter.value}</span>
                      <button
                        onClick={filter.onRemove}
                        className="ml-0.5 hover:opacity-70 transition-opacity"
                      >
                        <i className="fas fa-times text-[10px]"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ansicht Controls */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-3">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 block mb-2">
                Ansicht
              </span>
              <div className="flex flex-wrap gap-2 mb-2.5">
                {[
                  { value: "all", label: "Alles", icon: "fa-th-list" },
                  { value: "schedule", label: "Nur Zeitplan", icon: "fa-calendar-alt" },
                  { value: "results", label: "Nur Ergebnisse", icon: "fa-trophy" }
                ].map((mode) => (
                  <button
                    key={mode.value}
                    className={`inline-flex items-center gap-1.5 h-8 px-3.5 text-xs font-medium rounded-xl transition-colors ${selectedViewMode === mode.value
                      ? 'bg-primary dark:bg-primary-light text-white border border-primary dark:border-primary-light'
                      : 'bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-primary/50 dark:hover:border-primary-light/50'
                      }`}
                    onClick={() => setSelectedViewMode(mode.value)}
                  >
                    <i className={`fas ${mode.icon} text-[10px]`}></i>
                    {mode.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl text-xs font-medium transition-colors border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-primary/50 dark:hover:border-primary-light/50 hover:text-primary dark:hover:text-primary-light"
                  onClick={handleExpandAllResults}
                >
                  <i className="fas fa-expand-alt text-[10px]"></i>
                  Alle ausklappen
                </button>
                <button
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl text-xs font-medium transition-colors border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-primary/50 dark:hover:border-primary-light/50 hover:text-primary dark:hover:text-primary-light"
                  onClick={handleCollapseAllResults}
                >
                  <i className="fas fa-compress-alt text-[10px]"></i>
                  Alle einklappen
                </button>
                <button
                  className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl text-xs font-medium transition-colors border ${showAllJudgeScores
                    ? 'bg-primary/10 dark:bg-primary-light/10 border-primary/30 dark:border-primary-light/30 text-primary dark:text-primary-light'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-primary/50 dark:hover:border-primary-light/50 hover:text-primary dark:hover:text-primary-light'
                  }`}
                  onClick={() => handleToggleShowAllJudgeScores(!showAllJudgeScores)}
                >
                  {showAllJudgeScores && <i className="fas fa-check text-[10px]"></i>}
                  <i className="fas fa-list-ul text-[10px]"></i>
                  Detail-Punkte
                </button>
              </div>
            </div>
          </div>

          {/* Results View - Schedule Style */}
          {Object.keys(dataByLocation).length === 0 || Object.values(dataByLocation).every(events => events.length === 0) ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-trophy text-slate-400 dark:text-slate-500 text-xl"></i>
              </div>
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1.5">Keine Ergebnisse vorhanden</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                {searchTerm || selectedCategory !== "all" || selectedAgeGroup !== "all" || selectedGender !== "all" || selectedLocation !== "all"
                  ? "Filter anpassen oder Suchbegriff ändern."
                  : "Ergebnisse werden angezeigt, sobald Wettbewerbe abgeschlossen oder geplant sind."
                }
              </p>
            </div>
          ) : (
            <div className={`${selectedLocation !== "all" ? 'grid grid-cols-1' : 'grid grid-cols-1 lg:grid-cols-2'} gap-6`}>
              {Object.entries(dataByLocation).map(([location, events]) => (
                events.length > 0 && (
                  <div key={location} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                    {/* Venue Header */}
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-map-marker-alt text-xs"></i>
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{location}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                              <i className="fas fa-calendar-alt text-[9px]"></i>
                              {events.length} Events
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                              <i className="fas fa-users text-[9px]"></i>
                              {getTotalParticipants(events)} Athleten
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Events Timeline */}
                    <div className="p-5">
                      {events.length === 0 ? (
                        <div className="text-center py-10">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-trophy text-slate-400 dark:text-slate-500 text-lg"></i>
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{t("admin.results.noEvents", "Keine Events an dieser Fläche")}</p>
                        </div>
                      ) : (
                        <div className="space-y-5">
                          {events.map((event) => {
                            const isStatusUpdated = updatedValues.has(`status-${event.id}`);
                            return (
                              <div
                                key={event.id}
                                className={`bg-white dark:bg-slate-800 border border-l-4 rounded-2xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md ${
                                  event.status === 'Aktiv'
                                    ? 'border-slate-200 dark:border-slate-700 border-l-emerald-500 dark:border-l-emerald-400 shadow-md'
                                    : event.status === 'Vorbereitung'
                                      ? 'border-slate-200 dark:border-slate-700 border-l-orange-400'
                                      : event.status === 'Ausstehend'
                                        ? 'border-slate-200 dark:border-slate-700 border-l-yellow-400'
                                        : 'border-slate-200 dark:border-slate-700 border-l-slate-300 dark:border-l-slate-600'
                                } ${isStatusUpdated ? 'ring-2 ring-primary/30 dark:ring-primary-light/30' : ''}`}
                              >
                                {/* Header mit Status und Zeit */}
                                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700/60">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {eventSequenceNumbers[event.id] && (
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 border border-slate-300 dark:border-slate-500">
                                          {eventSequenceNumbers[event.id]}
                                        </span>
                                      )}
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-500 ${event.status === 'Ausstehend' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-700/30 dark:border-yellow-300/30' :
                                        event.status === 'Aktiv' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-700/30 dark:border-emerald-300/30' :
                                          event.status === 'Vorbereitung' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-700/30 dark:border-orange-300/30' :
                                            event.status === 'Abgeschlossen' ? 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30' :
                                              'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30'
                                        } ${isStatusUpdated ? 'animate-bounce scale-110' : ''}`}>
                                        <i className={`fas ${event.status === 'Aktiv' ? 'fa-play' :
                                          event.status === 'Ausstehend' ? 'fa-clock' :
                                            event.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                              'fa-check'
                                          } mr-1.5 text-[10px]`}></i>
                                        {getStatusLabel(event.status)}
                                      </span>
                                      {isStatusUpdated && (
                                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 animate-pulse">
                                          <i className="fas fa-check-circle"></i>
                                        </span>
                                      )}
                                      <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1.5">
                                          <i className="fas fa-clock text-slate-500 dark:text-slate-400 text-xs"></i>
                                          {event.type === 'schedule' ? (
                                            <>
                                              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                {formatTime(event.startTime)}
                                              </span>
                                              <span className="text-slate-400 dark:text-slate-500">-</span>
                                              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                {formatTime(event.endTime)}
                                              </span>
                                            </>
                                          ) : (
                                            <>
                                              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                {event.status === 'Aktiv' ? 'Live-Ergebnisse' : 'Endergebnis'}
                                              </span>
                                              {event.status === 'Aktiv' && (() => {
                                                const groupId = event.group?.id || event.id;
                                                const lastUpdate = lastUpdateTimes.get(groupId);
                                                return lastUpdate ? (
                                                  <>
                                                    <span className="text-slate-400 dark:text-slate-500">•</span>
                                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                                      <i className="fas fa-sync-alt mr-1"></i>
                                                      {formatTime(lastUpdate.toISOString())}
                                                    </span>
                                                  </>
                                                ) : null;
                                              })()}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {/* Dauer-Badge nur für schedule-Events */}
                                      {event.type === 'schedule' && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-600/30 dark:border-slate-400/30">
                                          <i className="fas fa-hourglass-half mr-1.5 text-[10px]"></i>
                                          {calculateGroupDuration(event.startTime, event.endTime)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Event Content */}
                                <div className="p-4 pt-3.5">
                                  <div className="space-y-3">
                                    {/* Kategorie mit Name und Metadaten */}
                                    <div className="space-y-1.5">
                                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                        Kategorie
                                      </label>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {/* Name als Badge */}
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-600/30 dark:border-slate-400/30">
                                          {(() => {
                                            const fullName = event.eventName || 'Unbekanntes Event';
                                            const dashIndex = fullName.indexOf('-');
                                            return dashIndex > 0 ? fullName.substring(0, dashIndex).trim() : fullName;
                                          })()}
                                        </span>
                                        {/* Metadaten als Text mit Icons, durch Punkt getrennt */}
                                        <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                                          <span className="text-slate-400 dark:text-slate-500">•</span>
                                          {/* Altersgruppe */}
                                          <div className="flex items-center gap-1">
                                            <i className="fas fa-calendar-alt text-[10px]"></i>
                                            <span>{event.ageGroup || 'Unbekannt'}</span>
                                          </div>
                                          <span className="text-slate-400 dark:text-slate-500">•</span>
                                          {/* Geschlecht */}
                                          <div className="flex items-center gap-1">
                                            <i className={`fas ${event.gender === 'männlich' ? 'fa-mars' : event.gender === 'weiblich' ? 'fa-venus' : 'fa-venus-mars'} text-[10px]`}></i>
                                            <span>{event.gender || 'Unbekannt'}</span>
                                          </div>
                                          {(() => {
                                            // Parallel-Attribut nur bei nicht abgeschlossenen Events anzeigen
                                            if (event.status === 'Abgeschlossen') return null;
                                            const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                            const twoParallel = eventInSchedule?.group?.twoParallel;
                                            if (twoParallel !== undefined) {
                                              return (
                                                <>
                                                  <span className="text-slate-400 dark:text-slate-500">•</span>
                                                  <div className="flex items-center gap-1">
                                                    <i className={`fas ${twoParallel ? 'fa-users' : 'fa-user'} text-[10px]`}></i>
                                                    <span>{twoParallel ? "2 parallel" : "Alleine"}</span>
                                                  </div>
                                                </>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Teilnehmeranzahl und Ausklappen-Button */}
                                    {event.type === 'schedule' ? (
                                      <div className="flex items-center justify-between pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
                                        <div className="flex items-center gap-2">
                                          <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center border border-slate-200 dark:border-slate-600">
                                            <i className="fas fa-users text-slate-500 dark:text-slate-400 text-[10px]"></i>
                                          </div>
                                          <div>
                                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 leading-none mb-0.5">
                                              Teilnehmer
                                            </label>
                                            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                              {(() => {
                                                const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                                const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' ||
                                                  eventInSchedule?.group?.isTeamCompetition ||
                                                  eventInSchedule?.group?.id === 'team-competition');
                                                const isPartnerForm = eventInSchedule?.group?.category?.type === 'Partnerform';
                                                const partnerTeamsForGroup = partnerTeamsData.get(eventInSchedule?.group?.id);
                                                if (isTeamEvent && teamData && teamData.currentParticipants) return `${teamData.currentParticipants} Teilnehmer`;
                                                if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) return `${partnerTeamsForGroup.partnerTeams.length} Teams`;
                                                return `${event.participants.length} Teilnehmer`;
                                              })()}
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-medium transition-colors border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:border-primary/50 dark:hover:border-primary-light/50 hover:text-primary dark:hover:text-primary-light"
                                          onClick={() => handleToggleEventExpand(event.id)}
                                        >
                                          <i className={`fas ${expandedEvents.has(event.id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`}></i>
                                          {expandedEvents.has(event.id) ? "Ausblenden" : "Anzeigen"}
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
                                        <div className="flex items-center gap-2">
                                          <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center border border-slate-200 dark:border-slate-600">
                                            <i className="fas fa-users text-slate-500 dark:text-slate-400 text-[10px]"></i>
                                          </div>
                                          <div>
                                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 leading-none mb-0.5">
                                              Teilnehmer
                                            </label>
                                            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                              {(() => {
                                                const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                                const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' ||
                                                  eventInSchedule?.group?.isTeamCompetition ||
                                                  eventInSchedule?.group?.id === 'team-competition');
                                                const isPartnerForm = eventInSchedule?.group?.category?.type === 'Partnerform';
                                                const partnerTeamsForGroup = partnerTeamsData.get(eventInSchedule?.group?.id);
                                                if (isTeamEvent && teamData && teamData.currentParticipants) return `${teamData.currentParticipants} Teilnehmer`;
                                                if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) return `${partnerTeamsForGroup.partnerTeams.length} Teams`;
                                                return `${event.participants.length} Teilnehmer`;
                                              })()}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Participants/Results */}
                                    {event.type === 'schedule' && (
                                      <>
                                        {expandedEvents.has(event.id) && (
                                          <div className="pt-3 mt-1 border-t border-slate-100 dark:border-slate-700/60">
                                            <div className="flex items-center justify-between mb-2.5">
                                              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                                Teilnehmerliste
                                              </label>
                                            </div>
                                            {renderParticipantsList(event)}
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {(event.type === 'result' || event.status === 'Aktiv') && (() => {
                                      // Sortiere Teilnehmer: Normale Teilnehmer nach Punkten, dann DNS-Teilnehmer
                                      const sortedParticipants = [...event.participants].sort((a, b) => {
                                        const aDNS = a.didNotStart || false;
                                        const bDNS = b.didNotStart || false;

                                        // DNS immer ganz unten
                                        if (aDNS && !bDNS) return 1;
                                        if (!aDNS && bDNS) return -1;

                                        // Innerhalb der Kategorien nach Punkten/Rang
                                        if (!aDNS && !bDNS) {
                                          if (a.rank && b.rank) return a.rank - b.rank;
                                          if (a.score !== undefined && b.score !== undefined) {
                                            return (b.score || 0) - (a.score || 0);
                                          }
                                        }

                                        return 0;
                                      });

                                      // Track Position-Änderungen für Animationen
                                      if (!previousPositionsRef.current.has(event.id)) {
                                        previousPositionsRef.current.set(event.id, new Map());
                                      }
                                      const prevPositions = previousPositionsRef.current.get(event.id);
                                      const currentPositions = new Map();

                                      sortedParticipants.forEach((p, index) => {
                                        currentPositions.set(p.id, index);
                                      });

                                      // Prüfe auf Position-Änderungen
                                      const hasPositionChanges = Array.from(currentPositions.entries()).some(([id, pos]) => {
                                        return prevPositions.get(id) !== undefined && prevPositions.get(id) !== pos;
                                      });

                                      if (hasPositionChanges) {
                                        // Positionen haben sich geändert - aktualisiere Ref nach kurzer Verzögerung für Animation
                                        setTimeout(() => {
                                          previousPositionsRef.current.set(event.id, currentPositions);
                                        }, 50);
                                      } else {
                                        previousPositionsRef.current.set(event.id, currentPositions);
                                      }

                                      // Berechne Position-Änderung für jeden Teilnehmer
                                      const getPositionChange = (participantId) => {
                                        const prevPos = prevPositions.get(participantId);
                                        const currentPos = currentPositions.get(participantId);

                                        if (prevPos === undefined || prevPos === currentPos) {
                                          return null;
                                        }

                                        return {
                                          from: prevPos,
                                          to: currentPos,
                                          direction: currentPos < prevPos ? 'up' : 'down'
                                        };
                                      };

                                      // Prüfe ob es eine Partnerform ist
                                      const isPartnerForm = event.isPartnerForm || event.group?.category?.type === 'Partnerform';

                                      // Alle Teilnehmer zusammen (inkl. DNS) - DNS werden normal in der Liste angezeigt
                                      const allParticipants = sortedParticipants;

                                      // Top 3 immer anzeigen, Rest ausklappbar
                                      const top3Participants = allParticipants.slice(0, 3);
                                      const restParticipants = allParticipants.slice(3);
                                      const isResultsExpanded = expandedResults.has(event.id);

                                      return (
                                        <div className="pt-3 mt-1 border-t border-slate-100 dark:border-slate-700/60">
                                          <div className="space-y-3">
                                            {/* Top 3 Teilnehmer / Partner-Teams - immer sichtbar */}
                                            {top3Participants.map((participant) => {
                                              const positionChange = getPositionChange(participant.id);
                                              const isMoving = positionChange !== null;
                                              const animationClass = isMoving
                                                ? (positionChange.direction === 'up' ? 'animate-slide-up' : 'animate-slide-down')
                                                : '';

                                              // Spezielle Darstellung für Partner-Teams
                                              if (participant.isPartnerTeam && participant.participant1 && participant.participant2) {
                                                const partnerName1 = participant.participant1?.name || 'Unbekannt';
                                                const partnerName2 = participant.participant2?.name || 'Unbekannt';
                                                const partnerNationCode = participant.participant1?.nation?.code || 'DE';
                                                const partnerNationName = participant.participant1?.nation?.name || 'Nation';

                                                return (
                                                  <div
                                                    key={participant.id}
                                                    className={`${animationClass} ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv')
                                                      ? participant.rank === 1
                                                        ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                                        : participant.rank === 2
                                                          ? 'bg-slate-100 dark:bg-slate-700/40'
                                                          : participant.rank === 3
                                                            ? 'bg-orange-50 dark:bg-orange-900/20'
                                                            : 'bg-white dark:bg-slate-800'
                                                      : 'bg-white dark:bg-slate-800'
                                                      } ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv') && !participant.didNotStart
                                                          ? participant.rank === 1 ? 'border-l-4 border-l-amber-400 dark:border-l-amber-500'
                                                            : participant.rank === 2 ? 'border-l-4 border-l-slate-400 dark:border-l-slate-500'
                                                            : participant.rank === 3 ? 'border-l-4 border-l-orange-400 dark:border-l-orange-500'
                                                            : '' : ''} ${participant.didNotStart ? 'opacity-50 grayscale' : ''} rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300 cursor-pointer`}
                                                  onClick={() => handleToggleJudgeScores(participant.id)}
                                                  >
                                                    <ParticipantResultCardLayout
                                                      expanded={expandedJudgeScores.has(participant.id)}
                                                      rankBadge={
                                                        <span
                                                          className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-all duration-300 ${updatedValues.has(`rank-${participant.id}-${event.id}`) ? "animate-pulse bg-green-100 dark:bg-green-900/30" : ""} ${
                                                            participant.rank === 1
                                                              ? "bg-yellow-200 text-yellow-900 dark:bg-yellow-800/60 dark:text-yellow-200"
                                                              : participant.rank === 2
                                                                ? "bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-200"
                                                                : participant.rank === 3
                                                                  ? "bg-orange-200 text-orange-900 dark:bg-orange-800/60 dark:text-orange-200"
                                                                  : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                                                          }`}
                                                        >
                                                          {getRankIcon(participant.rank)}
                                                        </span>
                                                      }
                                                      flagImg={
                                                        <img
                                                          src={
                                                            process.env.PUBLIC_URL +
                                                            "/images/flags/" +
                                                            partnerNationCode +
                                                            ".png"
                                                          }
                                                          alt={partnerNationName}
                                                          className="h-9 w-9 flex-shrink-0 rounded-full border border-slate-200 object-cover dark:border-slate-600 sm:h-8 sm:w-8"
                                                        />
                                                      }
                                                      body={
                                                        <>
                                                          <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                                                            <span className="min-w-0 break-words text-pretty">
                                                              {partnerName1} & {partnerName2}
                                                            </span>
                                                            {participant.didNotStart && (
                                                              <span className="inline-flex items-center rounded-full border border-red-700/30 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:border-red-300/30 dark:bg-red-900/30 dark:text-red-300">
                                                                <i className="fas fa-times-circle mr-1 text-[10px]" aria-hidden />
                                                                {t("results.didNotStart", "DNS")}
                                                              </span>
                                                            )}
                                                            {participant.isLate && !participant.didNotStart && (
                                                              <span className="inline-flex items-center rounded-full border border-yellow-700/30 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:border-yellow-300/30 dark:bg-yellow-900/30 dark:text-yellow-300">
                                                                <i className="fas fa-clock mr-1 text-[10px]" aria-hidden />
                                                                {t("scorekeeper.late", "Verspätet")}
                                                              </span>
                                                            )}
                                                          </div>
                                                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                            {partnerNationName}
                                                          </div>
                                                        </>
                                                      }
                                                      scoreBadge={
                                                        <span
                                                          className={`inline-flex min-h-[2.25rem] min-w-[3.25rem] items-center justify-center rounded-xl px-3 py-1.5 text-base font-bold tabular-nums transition-all duration-300 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1 sm:text-sm ${
                                                            updatedValues.has(`score-${participant.id}-${event.id}`)
                                                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                                                              : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                  !participant.didNotStart &&
                                                                  participant.rank === 1
                                                                ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                                                                : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                    !participant.didNotStart &&
                                                                    participant.rank === 2
                                                                  ? "bg-slate-200 text-slate-800 dark:bg-slate-600/60 dark:text-slate-200"
                                                                  : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                      !participant.didNotStart &&
                                                                      participant.rank === 3
                                                                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                                                                    : "bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
                                                          }`}
                                                        >
                                                          {participant.score}
                                                        </span>
                                                      }
                                                    />
                                                    {expandedJudgeScores.has(participant.id) && (
                                                      <div className="px-4 pb-1">
                                                        <JudgeScoresPanel participant={participant} event={event} updatedValues={updatedValues} />
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              }

                                              // Normale Teilnehmer-Darstellung
                                              return (
                                                <div
                                                  key={participant.id}
                                                  className={`${animationClass} ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv')
                                                    ? participant.rank === 1
                                                      ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                                      : participant.rank === 2
                                                        ? 'bg-slate-100 dark:bg-slate-700/40'
                                                        : participant.rank === 3
                                                          ? 'bg-orange-50 dark:bg-orange-900/20'
                                                          : 'bg-white dark:bg-slate-800'
                                                    : 'bg-white dark:bg-slate-800'
                                                    } ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv') && !participant.didNotStart
                                                        ? participant.rank === 1 ? 'border-l-4 border-l-amber-400 dark:border-l-amber-500'
                                                          : participant.rank === 2 ? 'border-l-4 border-l-slate-400 dark:border-l-slate-500'
                                                          : participant.rank === 3 ? 'border-l-4 border-l-orange-400 dark:border-l-orange-500'
                                                          : '' : ''} ${participant.didNotStart ? 'opacity-50 grayscale' : ''} rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300 cursor-pointer`}
                                                  onClick={() => handleToggleJudgeScores(participant.id)}
                                                >
                                                  <ParticipantResultCardLayout
                                                    expanded={expandedJudgeScores.has(participant.id)}
                                                    rankBadge={
                                                      <span
                                                        className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-all duration-300 ${updatedValues.has(`rank-${participant.id}-${event.id}`) ? "animate-pulse bg-green-100 dark:bg-green-900/30" : ""} ${
                                                          participant.rank === 1
                                                            ? "bg-yellow-200 text-yellow-900 dark:bg-yellow-800/60 dark:text-yellow-200"
                                                            : participant.rank === 2
                                                              ? "bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-200"
                                                              : participant.rank === 3
                                                                ? "bg-orange-200 text-orange-900 dark:bg-orange-800/60 dark:text-orange-200"
                                                                : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                                                        }`}
                                                      >
                                                        {getRankIcon(participant.rank)}
                                                      </span>
                                                    }
                                                    flagImg={
                                                      <img
                                                        src={
                                                          process.env.PUBLIC_URL +
                                                          "/images/flags/" +
                                                          participant.nationCode +
                                                          ".png"
                                                        }
                                                        alt={participant.nation}
                                                        className="h-9 w-9 flex-shrink-0 rounded-full border border-slate-200 object-cover dark:border-slate-600 sm:h-8 sm:w-8"
                                                      />
                                                    }
                                                    body={
                                                      <>
                                                        <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                                                          <span className="min-w-0 break-words text-pretty">
                                                            {participant.name}
                                                          </span>
                                                          {participant.didNotStart && (
                                                            <span className="inline-flex items-center rounded-full border border-red-700/30 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:border-red-300/30 dark:bg-red-900/30 dark:text-red-300">
                                                              <i className="fas fa-times-circle mr-1 text-[10px]" aria-hidden />
                                                              {t("results.didNotStart", "DNS")}
                                                            </span>
                                                          )}
                                                          {participant.isLate && !participant.didNotStart && (
                                                            <span className="inline-flex items-center rounded-full border border-yellow-700/30 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:border-yellow-300/30 dark:bg-yellow-900/30 dark:text-yellow-300">
                                                              <i className="fas fa-clock mr-1 text-[10px]" aria-hidden />
                                                              {t("scorekeeper.late", "Verspätet")}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                                                          <span className="break-words">{participant.nation}</span>
                                                          {participant.isTeam && participant.teamSize && (
                                                            <span className="whitespace-nowrap">
                                                              · {participant.teamSize} Teilnehmer
                                                            </span>
                                                          )}
                                                        </div>
                                                      </>
                                                    }
                                                    scoreBadge={
                                                      <span
                                                        className={`inline-flex min-h-[2.25rem] min-w-[3.25rem] items-center justify-center rounded-xl px-3 py-1.5 text-base font-bold tabular-nums transition-all duration-300 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1 sm:text-sm ${
                                                          updatedValues.has(`score-${participant.id}-${event.id}`)
                                                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                                                            : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                !participant.didNotStart &&
                                                                participant.rank === 1
                                                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                                                              : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                  !participant.didNotStart &&
                                                                  participant.rank === 2
                                                                ? "bg-slate-200 text-slate-800 dark:bg-slate-600/60 dark:text-slate-200"
                                                                : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                    !participant.didNotStart &&
                                                                    participant.rank === 3
                                                                  ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                                                                  : "bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
                                                        }`}
                                                      >
                                                        {participant.score}
                                                      </span>
                                                    }
                                                  />
                                                  {expandedJudgeScores.has(participant.id) && (
                                                    <div className="px-4 pb-1">
                                                      <JudgeScoresPanel participant={participant} event={event} updatedValues={updatedValues} />
                                                    </div>
                                                  )}
                                                </div>
                                              )
                                            })}

                                            {/* Rest-Teilnehmer (ab Platz 4) - ausklappbar */}
                                            {restParticipants.length > 0 && (
                                              <>
                                                {isResultsExpanded && (
                                                  <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                                    {restParticipants.map((participant) => {
                                                      const positionChange = getPositionChange(participant.id);
                                                      const isMoving = positionChange !== null;
                                                      const animationClass = isMoving
                                                        ? (positionChange.direction === 'up' ? 'animate-slide-up' : 'animate-slide-down')
                                                        : '';

                                                      // Spezielle Darstellung für Partner-Teams
                                                      if (participant.isPartnerTeam && participant.participant1 && participant.participant2) {
                                                        const partnerName1 = participant.participant1?.name || 'Unbekannt';
                                                        const partnerName2 = participant.participant2?.name || 'Unbekannt';
                                                        const partnerNationCode = participant.participant1?.nation?.code || 'DE';
                                                        const partnerNationName = participant.participant1?.nation?.name || 'Nation';

                                                        return (
                                                          <div
                                                            key={participant.id}
                                                            className={`${animationClass} ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv')
                                                              ? participant.rank === 1
                                                                ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                                                : participant.rank === 2
                                                                  ? 'bg-slate-100 dark:bg-slate-700/40'
                                                                  : participant.rank === 3
                                                                    ? 'bg-orange-50 dark:bg-orange-900/20'
                                                                    : 'bg-white dark:bg-slate-800'
                                                              : 'bg-white dark:bg-slate-800'
                                                              } ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv') && !participant.didNotStart
                                                                  ? participant.rank === 1 ? 'border-l-4 border-l-amber-400 dark:border-l-amber-500'
                                                                    : participant.rank === 2 ? 'border-l-4 border-l-slate-400 dark:border-l-slate-500'
                                                                    : participant.rank === 3 ? 'border-l-4 border-l-orange-400 dark:border-l-orange-500'
                                                                    : '' : ''} ${participant.didNotStart ? 'opacity-50 grayscale' : ''} rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300 cursor-pointer`}
                                                          onClick={() => handleToggleJudgeScores(participant.id)}
                                                          >
                                                            <ParticipantResultCardLayout
                                                              expanded={expandedJudgeScores.has(participant.id)}
                                                              rankBadge={
                                                                <span
                                                                  className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-all duration-300 ${updatedValues.has(`rank-${participant.id}-${event.id}`) ? "animate-pulse bg-green-100 dark:bg-green-900/30" : ""} ${
                                                                    participant.rank === 1
                                                                      ? "bg-yellow-200 text-yellow-900 dark:bg-yellow-800/60 dark:text-yellow-200"
                                                                      : participant.rank === 2
                                                                        ? "bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-200"
                                                                        : participant.rank === 3
                                                                          ? "bg-orange-200 text-orange-900 dark:bg-orange-800/60 dark:text-orange-200"
                                                                          : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                                                                  }`}
                                                                >
                                                                  {getRankIcon(participant.rank)}
                                                                </span>
                                                              }
                                                              flagImg={
                                                                <img
                                                                  src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/images/flags/" +
                                                                    partnerNationCode +
                                                                    ".png"
                                                                  }
                                                                  alt={partnerNationName}
                                                                  className="h-9 w-9 flex-shrink-0 rounded-full border border-slate-200 object-cover dark:border-slate-600 sm:h-8 sm:w-8"
                                                                />
                                                              }
                                                              body={
                                                                <>
                                                                  <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                                                                    <span className="min-w-0 break-words text-pretty">
                                                                      {partnerName1} & {partnerName2}
                                                                    </span>
                                                                    {participant.didNotStart && (
                                                                      <span className="inline-flex items-center rounded-full border border-red-700/30 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:border-red-300/30 dark:bg-red-900/30 dark:text-red-300">
                                                                        <i className="fas fa-times-circle mr-1 text-[10px]" aria-hidden />
                                                                        {t("results.didNotStart", "DNS")}
                                                                      </span>
                                                                    )}
                                                                    {participant.isLate && !participant.didNotStart && (
                                                                      <span className="inline-flex items-center rounded-full border border-yellow-700/30 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:border-yellow-300/30 dark:bg-yellow-900/30 dark:text-yellow-300">
                                                                        <i className="fas fa-clock mr-1 text-[10px]" aria-hidden />
                                                                        {t("scorekeeper.late", "Verspätet")}
                                                                      </span>
                                                                    )}
                                                                  </div>
                                                                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                                    {partnerNationName}
                                                                  </div>
                                                                </>
                                                              }
                                                              scoreBadge={
                                                                <span
                                                                  className={`inline-flex min-h-[2.25rem] min-w-[3.25rem] items-center justify-center rounded-xl px-3 py-1.5 text-base font-bold tabular-nums transition-all duration-300 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1 sm:text-sm ${
                                                                    updatedValues.has(`score-${participant.id}-${event.id}`)
                                                                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                                                                      : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                          !participant.didNotStart &&
                                                                          participant.rank === 1
                                                                        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                                                                        : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                            !participant.didNotStart &&
                                                                            participant.rank === 2
                                                                          ? "bg-slate-200 text-slate-800 dark:bg-slate-600/60 dark:text-slate-200"
                                                                          : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                              !participant.didNotStart &&
                                                                              participant.rank === 3
                                                                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                                                                            : "bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
                                                                  }`}
                                                                >
                                                                  {participant.score}
                                                                </span>
                                                              }
                                                            />
                                                            {expandedJudgeScores.has(participant.id) && (
                                                              <div className="px-4 pb-1">
                                                                <JudgeScoresPanel participant={participant} event={event} updatedValues={updatedValues} />
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      }

                                                      // Normale Teilnehmer-Darstellung
                                                      return (
                                                        <div
                                                          key={participant.id}
                                                          className={`${animationClass} ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv')
                                                            ? participant.rank === 1
                                                              ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                                              : participant.rank === 2
                                                                ? 'bg-slate-100 dark:bg-slate-700/40'
                                                                : participant.rank === 3
                                                                  ? 'bg-orange-50 dark:bg-orange-900/20'
                                                                  : 'bg-white dark:bg-slate-800'
                                                            : 'bg-white dark:bg-slate-800'
                                                            } ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv') && !participant.didNotStart
                                                                ? participant.rank === 1 ? 'border-l-4 border-l-amber-400 dark:border-l-amber-500'
                                                                  : participant.rank === 2 ? 'border-l-4 border-l-slate-400 dark:border-l-slate-500'
                                                                  : participant.rank === 3 ? 'border-l-4 border-l-orange-400 dark:border-l-orange-500'
                                                                  : '' : ''} ${participant.didNotStart ? 'opacity-50 grayscale' : ''} rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300 cursor-pointer`}
                                                          onClick={() => handleToggleJudgeScores(participant.id)}
                                                        >
                                                          <ParticipantResultCardLayout
                                                            expanded={expandedJudgeScores.has(participant.id)}
                                                            rankBadge={
                                                              <span
                                                                className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-all duration-300 ${updatedValues.has(`rank-${participant.id}-${event.id}`) ? "animate-pulse bg-green-100 dark:bg-green-900/30" : ""} ${
                                                                  participant.rank === 1
                                                                    ? "bg-yellow-200 text-yellow-900 dark:bg-yellow-800/60 dark:text-yellow-200"
                                                                    : participant.rank === 2
                                                                      ? "bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-200"
                                                                      : participant.rank === 3
                                                                        ? "bg-orange-200 text-orange-900 dark:bg-orange-800/60 dark:text-orange-200"
                                                                        : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                                                                }`}
                                                              >
                                                                {getRankIcon(participant.rank)}
                                                              </span>
                                                            }
                                                            flagImg={
                                                              <img
                                                                src={
                                                                  process.env.PUBLIC_URL +
                                                                  "/images/flags/" +
                                                                  participant.nationCode +
                                                                  ".png"
                                                                }
                                                                alt={participant.nation}
                                                                className="h-9 w-9 flex-shrink-0 rounded-full border border-slate-200 object-cover dark:border-slate-600 sm:h-8 sm:w-8"
                                                              />
                                                            }
                                                            body={
                                                              <>
                                                                <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                                                                  <span className="min-w-0 break-words text-pretty">
                                                                    {participant.name}
                                                                  </span>
                                                                  {participant.didNotStart && (
                                                                    <span className="inline-flex items-center rounded-full border border-red-700/30 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:border-red-300/30 dark:bg-red-900/30 dark:text-red-300">
                                                                      <i className="fas fa-times-circle mr-1 text-[10px]" aria-hidden />
                                                                      {t("results.didNotStart", "DNS")}
                                                                    </span>
                                                                  )}
                                                                  {participant.isLate && !participant.didNotStart && (
                                                                    <span className="inline-flex items-center rounded-full border border-yellow-700/30 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:border-yellow-300/30 dark:bg-yellow-900/30 dark:text-yellow-300">
                                                                      <i className="fas fa-clock mr-1 text-[10px]" aria-hidden />
                                                                      {t("scorekeeper.late", "Verspätet")}
                                                                    </span>
                                                                  )}
                                                                </div>
                                                                <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                                                                  <span className="break-words">{participant.nation}</span>
                                                                  {participant.isTeam && participant.teamSize && (
                                                                    <span className="whitespace-nowrap">
                                                                      · {participant.teamSize} Teilnehmer
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              </>
                                                            }
                                                            scoreBadge={
                                                              <span
                                                                className={`inline-flex min-h-[2.25rem] min-w-[3.25rem] items-center justify-center rounded-xl px-3 py-1.5 text-base font-bold tabular-nums transition-all duration-300 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1 sm:text-sm ${
                                                                  updatedValues.has(`score-${participant.id}-${event.id}`)
                                                                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                                                                    : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                        !participant.didNotStart &&
                                                                        participant.rank === 1
                                                                      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                                                                      : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                          !participant.didNotStart &&
                                                                          participant.rank === 2
                                                                        ? "bg-slate-200 text-slate-800 dark:bg-slate-600/60 dark:text-slate-200"
                                                                        : (event.status === "Abgeschlossen" || event.status === "Aktiv") &&
                                                                            !participant.didNotStart &&
                                                                            participant.rank === 3
                                                                          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                                                                          : "bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
                                                                }`}
                                                              >
                                                                {participant.score}
                                                              </span>
                                                            }
                                                          />
                                                          {expandedJudgeScores.has(participant.id) && (
                                                            <div className="px-4 pb-1">
                                                              <JudgeScoresPanel participant={participant} event={event} updatedValues={updatedValues} />
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}

                                                {/* Ausklappen-Button für Rest-Teilnehmer */}
                                                <div className="pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
                                                  <button
                                                    onClick={() => handleToggleResultsExpand(event.id)}
                                                    className="w-full flex items-center justify-center gap-1.5 h-8 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:bg-slate-50 dark:hover:bg-slate-700/40 rounded-xl transition-colors"
                                                  >
                                                    <i className={`fas ${isResultsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`}></i>
                                                    {isResultsExpanded
                                                      ? `${restParticipants.length} weitere ausblenden`
                                                      : `${restParticipants.length} weitere anzeigen`}
                                                  </button>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                        </div>

                      )}
                    </div>

                  </div>

                )))}
            </div>

          )}



        </div>
      </main>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />
    </div>
  );
};

export default ResultsTailwind;
